import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../utils/AuthContext";
import API from "../utils/API";
import AddTaskModal from "../components/AddTaskModal";
import TaskDetailModal from "../components/TaskDetailModal";
import CreateFeedbackRequestModal from "../components/CreateFeedbackRequestModal";
import EditLinkedOrgModal from "../components/EditLinkedOrgModal";
import ReportDetailModal from "../components/ReportDetailModal";
import WorkspaceRulemakingList from "../components/WorkspaceRulemakingList";
import PageTitle from "../components/PageTitle";
import "./teams.css";
import { getCurrentSession } from "../utils/session";

const CLIENT_LABEL_TYPES = ['Lobbyists', 'Political Firms', 'PR Firms', 'Other'];

const TODAY_PT = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

// Bills.session is stored undashed (e.g. "20252026"); display/filter state uses
// the dashed form (e.g. "2025-2026") to match getCurrentSession().
const formatSessionDashed = (rawSession) => {
    const match = String(rawSession || '').match(/^(\d{4})(\d{4})$/);
    return match ? `${match[1]}-${match[2]}` : (rawSession || '');
};

const TAG_COLOR_OPTIONS = [
  { key: 'purple', bg: '#e8e4f4', text: '#3d2e7a', label: 'Purple' },
  { key: 'teal',   bg: '#cfe8ef', text: '#1a4a5a', label: 'Teal' },
  { key: 'sage',   bg: '#d5eddf', text: '#1a4a2e', label: 'Sage' },
  { key: 'amber',  bg: '#fdebd0', text: '#6b3d0a', label: 'Amber' },
  { key: 'rose',   bg: '#fcd9e2', text: '#6b1a2e', label: 'Rose' },
  { key: 'slate',  bg: '#dde3ec', text: '#2b3a4c', label: 'Slate' },
  { key: 'indigo', bg: '#dce0f5', text: '#2d2a7e', label: 'Indigo' },
  { key: 'coral',  bg: '#fce0d8', text: '#6b2a1a', label: 'Coral' },
];

const BLANK_CONTACT = { first_name: '', last_name: '', title: '', phone: '', email: '', address: '', notes: '' };

const WORKSPACE_ACCESS_LEVELS = [
  {
    key: 'owner',
    label: 'Owner',
    icon: 'bi-star-fill',
    summary: 'The member who created the Workspace. There is exactly one per Workspace.',
    can: [
      'Everything an Admin can do, plus:',
      'Rename, re-color, or delete the Workspace',
      'Grant or revoke Admin/Editor access for any other member',
      'Create, rename, and delete Workspace-shared Saved Filters',
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    icon: 'bi-shield-check',
    summary: 'A member with "Account Manage" permission, granted by the Owner.',
    can: [
      'Add, remove, and manage permissions for other members (except the Owner)',
      'Add, edit, and archive Clients/Stakeholder Groups and their contacts',
      'Create and manage Subject Tags',
      'Assign or reassign any bill/task to any member, and edit any member’s notes',
      'Everything an Editor can do',
    ],
  },
  {
    key: 'editor',
    label: 'Editor',
    icon: 'bi-pencil-square',
    summary: 'A member with "Bill Manage" permission, granted by the Owner or an Admin.',
    can: [
      'Track/untrack bills for the Workspace and its Clients/Stakeholder Groups',
      'Create and send Client/Stakeholder Feedback Requests',
      'Apply Subject Tags to bills',
      'Manage tasks and assignments on bills they are the Lead on',
    ],
  },
  {
    key: 'viewer',
    label: 'Viewer',
    icon: 'bi-eye',
    summary: 'The default level for a new member with no additional permissions granted.',
    can: [
      'View the Workspace’s tracked bills, tasks, reports, and Clients/Stakeholder Groups',
      'Apply Workspace-shared Saved Filters on the Legislation page',
      'Manage tasks and assignments on bills they are the Lead on',
    ],
  },
];

const WORKSPACE_FEATURES = [
  {
    key: 'overview',
    icon: 'bi-people',
    label: 'Members & Organization',
    desc: 'Invite colleagues by email, set their access level, and link the Workspace to your Organization to auto-suggest eligible members. This is also where Clients/Stakeholder Groups (and their contacts) are added, edited, and archived.',
  },
  {
    key: 'feedback',
    icon: 'bi-send',
    label: 'Client / Stakeholder Feedback Requests',
    desc: 'Send a set of bills to a Client or Stakeholder Group’s contacts with a deadline, and collect their notes back on each bill right in the Workspace.',
  },
  {
    key: 'billwork',
    icon: 'bi-journal-check',
    label: 'Bill Work',
    desc: 'See every bill the Workspace (and each Client/Stakeholder Group) is tracking in one place, assign a Lead and other members to a bill, set position/priority, and tag bills by subject.',
  },
  {
    key: 'tasks',
    icon: 'bi-check2-square',
    label: 'All Tasks',
    desc: 'A rolled-up, filterable view of every task across every bill the Workspace is tracking, so nothing due soon gets missed.',
  },
  {
    key: 'reports',
    icon: 'bi-file-earmark-text',
    label: 'Reports',
    desc: 'Build a bill report for a Client/Stakeholder Group (or internal use) and send it to recipients, who view it through a link — no login required.',
  },
  {
    key: 'savedfilters',
    icon: 'bi-funnel',
    label: 'Saved Filters',
    desc: 'Legislation page filter presets shared with the whole Workspace, so everyone searches the same way — and they stay with the Workspace even if the member who created them leaves.',
  },
];

const WORKSPACE_SELECTION_TTL = 2 * 60 * 60 * 1000; // 2 hours

function getStoredTeamId(userId) {
  try {
    const raw = localStorage.getItem(`workspace_selection_${userId}`);
    if (!raw) return null;
    const { teamId, expiresAt } = JSON.parse(raw);
    if (!expiresAt || Date.now() > expiresAt) return null;
    return teamId;
  } catch {
    return null;
  }
}

function storeSelectedTeamId(userId, teamId) {
  try {
    localStorage.setItem(
      `workspace_selection_${userId}`,
      JSON.stringify({ teamId, expiresAt: Date.now() + WORKSPACE_SELECTION_TTL })
    );
  } catch {
    // ignore storage errors (e.g. private browsing)
  }
}

function formatPhoneInput(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatFilterSessionType(num) {
  if (num >= 1) return `${num}th Extraordinary`;
  return 'Regular';
}

// Turns a saved filter's raw filter_json into a list of readable { label, value } search
// parameters, resolving author/location codes to names where lookups are available.
function describeSavedFilterCriteria(filterJson, { authorNames, locationNames, currentTeam, clients }) {
  let f;
  try {
    f = typeof filterJson === 'string' ? JSON.parse(filterJson) : filterJson;
  } catch {
    return [];
  }
  if (!f) return [];

  const parts = [];
  if (f.search) parts.push({ label: 'Search', value: `"${f.search}"` });
  // Legacy saved filters (pre bill/subject merge) still stored these two separately.
  if (f.bill_id_search) parts.push({ label: 'Bill Search', value: `"${f.bill_id_search}"` });
  if (f.subject_search) parts.push({ label: 'Subject Search', value: `"${f.subject_search}"` });
  if (f.session) parts.push({ label: 'Session', value: f.session });
  if (f.session_number && f.session_number !== '0') {
    parts.push({ label: 'Session Type', value: formatFilterSessionType(Number(f.session_number)) });
  }
  if (f.author) parts.push({ label: 'Author', value: authorNames.get(String(f.author)) || `Legislator #${f.author}` });
  if (f.location) parts.push({ label: 'Location', value: locationNames.get(f.location) || f.location });
  if (f.status) parts.push({ label: 'Status', value: f.status });
  if (f.trackedBy === 'myself') {
    parts.push({ label: 'Tracked By', value: "Filter creator's individually tracked bills" });
  } else if (f.trackedBy && currentTeam && String(currentTeam.team_id) === String(f.trackedBy)) {
    parts.push({ label: 'Tracked By', value: `${currentTeam.team_name} (this Workspace)` });
  } else if (f.trackedBy) {
    parts.push({ label: 'Tracked By', value: 'Another Workspace' });
  }
  if (f.trackedByClient) {
    const client = (clients || []).find(c => String(c.client_id) === String(f.trackedByClient));
    parts.push({ label: 'Client', value: client?.client_name || `Client #${f.trackedByClient}` });
  }
  if (f.vote_required) parts.push({ label: 'Vote Required', value: f.vote_required });
  if (f.fiscal_committee) parts.push({ label: 'Fiscal Committee', value: f.fiscal_committee });
  if (f.local_program) parts.push({ label: 'Local Program', value: f.local_program });
  if (f.urgency) parts.push({ label: 'Urgency', value: f.urgency });
  if (f.tax_levy) parts.push({ label: 'Tax Levy', value: f.tax_levy });
  if (f.version_date_from || f.version_date_to) {
    parts.push({ label: 'Version Date Range', value: `${f.version_date_from || '…'} to ${f.version_date_to || '…'}` });
  }
  const activity = [];
  if (f.version_introduced === 'true') activity.push('Introduced');
  if (f.version_amended === 'true') activity.push('Amended');
  if (activity.length) parts.push({ label: 'Version Activity', value: activity.join(', ') });
  if (f.showDigest) parts.push({ label: 'Digest Only', value: 'Yes' });
  if (f.showAi) parts.push({ label: 'AI Summary Only', value: 'Yes' });
  return parts;
}

export default function Teams() {
    const { auth, workplaceSubscriber } = useContext(AuthContext);
    const [searchParams] = useSearchParams();
    const [myTeams, setMyTeams] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [newTeamName, setNewTeamName] = useState("");
    const [loading, setLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState("");
    const [showAboutWorkspaces, setShowAboutWorkspaces] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [eligibleMembers, setEligibleMembers] = useState([]);
    const [pendingInvite, setPendingInvite] = useState(null); // { profile, type }
    const [inviteSent, setInviteSent] = useState({}); // { [profile_id]: true }
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedTeamName, setEditedTeamName] = useState("");
    const [editedTeamAcronym, setEditedTeamAcronym] = useState("");
    const [editedTagColor, setEditedTagColor] = useState("");

    // Org association
    const [orgQuery, setOrgQuery] = useState("");
    const [orgResults, setOrgResults] = useState([]);
    const [orgSearchOpen, setOrgSearchOpen] = useState(false);
    const orgSearchRef = useRef(null);

    // Clients
    const [clients, setClients] = useState([]);
    const [newClientName, setNewClientName] = useState("");
    const [editingClientId, setEditingClientId] = useState(null);
    const [editingClientName, setEditingClientName] = useState("");
    const [expandedClientId, setExpandedClientId] = useState(null);

    // Contact form (per expanded client)
    const [contactForm, setContactForm] = useState(BLANK_CONTACT);
    const [editingContact, setEditingContact] = useState(null); // { contactId, form }
    const [showAddContact, setShowAddContact] = useState(false);

    // Org change mode
    const [isChangingOrg, setIsChangingOrg] = useState(false);

    // Rulemaking follow-up for enacted bills the Workspace tracks (null until the tab is first opened)
    const [teamRulemaking, setTeamRulemaking] = useState(null);
    const [teamRulemakingLoading, setTeamRulemakingLoading] = useState(false);
    // Tasks on rulemaking items (notices), loaded with the Rulemaking tab
    const [teamRulemakingTasks, setTeamRulemakingTasks] = useState([]);

    // Workspace saved legislation filter presets
    const [teamSavedFilters, setTeamSavedFilters] = useState([]);
    const [teamSavedFiltersLoading, setTeamSavedFiltersLoading] = useState(false);
    const [tsfEditingId, setTsfEditingId] = useState(null);
    const [tsfEditingName, setTsfEditingName] = useState('');
    const [filterAuthorNames, setFilterAuthorNames] = useState(new Map()); // legislators_tbl_id -> legislator_name
    const [filterLocationNames, setFilterLocationNames] = useState(new Map()); // location_code -> description

    // Client feedback requests
    const [feedbackRequests, setFeedbackRequests] = useState({}); // { [clientId]: [] }
    const [expandedFeedbackId, setExpandedFeedbackId] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);

    // Edit linked org
    const [editOrgData, setEditOrgData] = useState(null);

    // Archived clients toggle
    const [showArchivedClients, setShowArchivedClients] = useState(false);

    // Team detail tab
    const [teamTab, setTeamTab] = useState('overview');

    // Bill Work state
    const [trackedBillsOverview, setTrackedBillsOverview] = useState([]);
    const [billWorkLoading, setBillWorkLoading] = useState(false);
    const [expandedBillId, setExpandedBillId] = useState(null);
    const [billTasks, setBillTasks] = useState({});
    const [billNotes, setBillNotes] = useState({});
    const [newTaskText, setNewTaskText] = useState({});
    const [newTaskAssignee, setNewTaskAssignee] = useState({});
    const [newTaskDue, setNewTaskDue] = useState({});
    const [newTaskPriority, setNewTaskPriority] = useState({});
    const [newNoteText, setNewNoteText] = useState({});
    const [addAssignForm, setAddAssignForm] = useState({});  // { billId: { userId:'', role:'Monitor' } }
    const [showBulkReassign, setShowBulkReassign] = useState(false);
    const [bulkFrom, setBulkFrom] = useState('');
    const [bulkTo, setBulkTo] = useState('');
    const [bulkResult, setBulkResult] = useState(null);
    const [billAssigneeFilter, setBillAssigneeFilter] = useState('');
    const [teamSubjectTags, setTeamSubjectTags] = useState([]);
    const [billSubjectTagFilter, setBillSubjectTagFilter] = useState('');
    const [billClientFilter, setBillClientFilter] = useState('');
    const [billSessionFilter, setBillSessionFilter] = useState(getCurrentSession());
    const [showManageTags, setShowManageTags] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [addTagSelect, setAddTagSelect] = useState({});  // { billId: tagId }

    // Tasks dashboard state
    const [teamTasks, setTeamTasks] = useState([]);
    const [teamTasksLoading, setTeamTasksLoading] = useState(false);
    const [taskDashFilter, setTaskDashFilter] = useState('all');
    const [showAddTask, setShowAddTask] = useState(false);
    const [detailTaskId, setDetailTaskId] = useState(null);

    // Reports state
    const [reports, setReports] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState(null);

    // Ambassador program state
    const [ambassadorOverview, setAmbassadorOverview] = useState(null);
    const [ambassadorLoading, setAmbassadorLoading] = useState(false);
    const [myAmbassadorInfo, setMyAmbassadorInfo] = useState(null);

    const isOwner = selectedTeam?.team_admin === auth.user.user_id;
    const isClientLabelType = CLIENT_LABEL_TYPES.includes(selectedTeam?.Organization?.group_type);
    const sectionLabel = isClientLabelType ? 'Clients' : 'Stakeholder Groups';
    const sectionLabelSingular = isClientLabelType ? 'Client' : 'Stakeholder Group';

    const billWorkClientOptions = Object.values(
        trackedBillsOverview.reduce((acc, bill) => {
            for (const c of bill.clients || []) acc[c.client_id] = c;
            return acc;
        }, {})
    ).sort((a, b) => a.client_name.localeCompare(b.client_name));

    const billWorkSessionOptions = [...new Set([getCurrentSession(), ...trackedBillsOverview.map(b => formatSessionDashed(b.session)).filter(Boolean)])]
        .sort((a, b) => b.localeCompare(a));

    // Close org dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (orgSearchRef.current && !orgSearchRef.current.contains(e.target)) {
                setOrgSearchOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        fetchTeams();
    }, []);

    const fetchTeams = async () => {
        try {
            const res = await API.getUserTeams({user_id: auth.user.user_id});
            setMyTeams(res.data);
            const teamParam = searchParams.get('team');
            const storedTeamId = getStoredTeamId(auth.user.user_id);
            if (teamParam) {
                viewTeamDetails(Number(teamParam));
            } else if (res.data.length === 1) {
                viewTeamDetails(res.data[0].team_id);
            } else if (storedTeamId && res.data.some(m => m.team_id === storedTeamId)) {
                viewTeamDetails(storedTeamId);
            }
        } catch (err) {
            console.error("Error fetching teams:", err);
        } finally {
            setLoading(false);
        }
    };

    // ── Workspace Saved Filters ──────────────────────────────
    const fetchTeamRulemaking = async (teamId) => {
        setTeamRulemakingLoading(true);
        try {
            const [res, tasksRes] = await Promise.all([
                API.getTeamRulemaking(teamId, auth.user.user_id),
                API.getTeamRulemakingTasks(teamId, auth.user.user_id).catch(() => ({ data: [] })),
            ]);
            setTeamRulemakingTasks(tasksRes.data);
            setTeamRulemaking(res.data);
        } catch (err) {
            console.error('Error fetching Workspace rulemaking:', err);
            setTeamRulemaking({ bills: [], notices_available: true, failed: true });
        } finally {
            setTeamRulemakingLoading(false);
        }
    };

    const fetchTeamSavedFilters = async (teamId) => {
        setTeamSavedFiltersLoading(true);
        try {
            const res = await API.getTeamSavedFilters(teamId);
            setTeamSavedFilters(res.data);

            // Resolve human-readable Author/Location names for whichever sessions
            // these filters reference, so parameters can be displayed meaningfully.
            const sessions = new Set();
            res.data.forEach(preset => {
                try {
                    const parsed = typeof preset.filter_json === 'string' ? JSON.parse(preset.filter_json) : preset.filter_json;
                    if (parsed?.session) sessions.add(parsed.session);
                } catch { /* ignore unparsable preset */ }
            });
            if (sessions.size > 0) {
                const [authorEntries, locationEntries] = await Promise.all([
                    Promise.all([...sessions].map(session =>
                        axios.get(`/api/legislation/legislators?session=${session}`).then(r => r.data).catch(() => [])
                    )),
                    Promise.all([...sessions].map(session =>
                        axios.get(`/api/legislation/locations?session=${session}`).then(r => r.data).catch(() => [])
                    )),
                ]);
                setFilterAuthorNames(new Map(authorEntries.flat().map(a => [String(a.legislators_tbl_id), a.legislator_name])));
                setFilterLocationNames(new Map(locationEntries.flat().map(l => [l.location_code, l.description])));
            }
        } catch (err) {
            console.error('Error fetching Workspace saved filters:', err);
            setTeamSavedFilters([]);
        } finally {
            setTeamSavedFiltersLoading(false);
        }
    };

    const handleTsfDelete = async (filterId) => {
        try {
            await API.deleteTeamSavedFilter(selectedTeam.team_id, filterId, auth.user.user_id);
            setTeamSavedFilters(prev => prev.filter(p => p.filter_id !== filterId));
            if (tsfEditingId === filterId) setTsfEditingId(null);
        } catch (err) {
            console.error('Error deleting Workspace saved filter:', err);
        }
    };

    const handleTsfRename = async (filterId) => {
        if (!tsfEditingName.trim()) return;
        try {
            await API.updateTeamSavedFilter(selectedTeam.team_id, filterId, auth.user.user_id, tsfEditingName.trim());
            setTeamSavedFilters(prev =>
                prev.map(p => p.filter_id === filterId ? { ...p, name: tsfEditingName.trim() } : p)
                    .sort((a, b) => a.name.localeCompare(b.name))
            );
            setTsfEditingId(null);
            setTsfEditingName('');
        } catch (err) {
            console.error('Error renaming Workspace saved filter:', err);
        }
    };

    const fetchClients = async (teamId) => {
        try {
            const res = await API.getTeamClients(teamId);
            setClients(res.data);
        } catch (err) {
            console.error("Error fetching clients:", err);
        }
    };

    // Reload clients when selected team changes
    useEffect(() => {
        if (selectedTeam) {
            fetchClients(selectedTeam.team_id);
        } else {
            setClients([]);
        }
        setExpandedClientId(null);
        setShowAddContact(false);
        setEditingContact(null);
        setIsChangingOrg(false);
        setOrgQuery("");
        setOrgResults([]);
        setTrackedBillsOverview([]);
        setExpandedBillId(null);
        setBillTasks({});
        setBillNotes({});
        setShowBulkReassign(false);
        setBulkFrom('');
        setBulkTo('');
        setBulkResult(null);
        setBillAssigneeFilter('');
        setTeamTasks([]);
        setReports([]);
        setSelectedReportId(null);
        setTeamSavedFilters([]);
        setTsfEditingId(null);
        setTeamRulemaking(null);
        setTeamRulemakingTasks([]);
        setAmbassadorOverview(null);
        setMyAmbassadorInfo(null);
        if (selectedTeam?.ambassador_enabled) {
            fetchMyAmbassadorInfo();
        }
        if (selectedTeam && searchParams.get('tab') === 'reports') {
            setTeamTab('reports');
            fetchReports(selectedTeam.team_id);
        } else if (selectedTeam && searchParams.get('tab') === 'savedfilters') {
            setTeamTab('savedfilters');
            fetchTeamSavedFilters(selectedTeam.team_id);
        } else {
            setTeamTab('overview');
        }
    }, [selectedTeam?.team_id]);

    // ── Org association ──────────────────────────────────────
    const handleOrgSearch = async (val) => {
        setOrgQuery(val);
        if (val.length >= 2) {
            const res = await API.searchOrganizations(val);
            setOrgResults(res.data);
            setOrgSearchOpen(true);
        } else {
            setOrgResults([]);
            setOrgSearchOpen(false);
        }
    };

    const handleSelectOrg = async (org) => {
        try {
            await API.setTeamOrganization(selectedTeam.team_id, {
                user_id: auth.user.user_id,
                organization_id: org.organization_id,
            });
            setOrgQuery("");
            setOrgResults([]);
            setOrgSearchOpen(false);
            setIsChangingOrg(false);
            const res = await API.getTeamDetails(selectedTeam.team_id);
            setSelectedTeam(res.data);
        } catch (err) {
            console.error("Error setting org:", err);
        }
    };

    const handleClearOrg = async () => {
        if (!window.confirm("Remove the organization association from this team?")) return;
        try {
            await API.setTeamOrganization(selectedTeam.team_id, {
                user_id: auth.user.user_id,
                organization_id: null,
            });
            const res = await API.getTeamDetails(selectedTeam.team_id);
            setSelectedTeam(res.data);
        } catch (err) {
            console.error("Error clearing org:", err);
        }
    };

    // ── Clients ──────────────────────────────────────────────
    const handleAddClient = async (e) => {
        e.preventDefault();
        if (!newClientName.trim()) return;
        try {
            await API.createTeamClient(selectedTeam.team_id, {
                user_id: auth.user.user_id,
                client_name: newClientName.trim(),
            });
            setNewClientName("");
            fetchClients(selectedTeam.team_id);
        } catch (err) {
            console.error("Error adding client:", err);
        }
    };

    const handleSaveClientName = async (clientId) => {
        if (!editingClientName.trim()) return;
        try {
            await API.updateTeamClient(selectedTeam.team_id, clientId, {
                user_id: auth.user.user_id,
                client_name: editingClientName.trim(),
            });
            setEditingClientId(null);
            fetchClients(selectedTeam.team_id);
        } catch (err) {
            console.error("Error renaming client:", err);
        }
    };

    const handleArchiveClient = async (clientId) => {
        try {
            await API.archiveTeamClient(selectedTeam.team_id, clientId, auth.user.user_id);
            if (expandedClientId === clientId) setExpandedClientId(null);
            fetchClients(selectedTeam.team_id);
        } catch (err) {
            console.error("Error archiving client:", err);
        }
    };

    const handleUnarchiveClient = async (clientId) => {
        try {
            await API.unarchiveTeamClient(selectedTeam.team_id, clientId, auth.user.user_id);
            fetchClients(selectedTeam.team_id);
        } catch (err) {
            console.error("Error unarchiving client:", err);
        }
    };

    const handleDeleteClient = async (clientId, clientName) => {
        if (!window.confirm(`Permanently delete "${clientName}"? This cannot be undone.`)) return;
        try {
            await API.deleteTeamClient(selectedTeam.team_id, clientId, auth.user.user_id);
            if (expandedClientId === clientId) setExpandedClientId(null);
            fetchClients(selectedTeam.team_id);
        } catch (err) {
            console.error("Error deleting client:", err);
        }
    };

    const loadFeedbackRequests = (clientId) => {
        if (!selectedTeam?.team_id) return;
        API.getFeedbackRequests(selectedTeam.team_id, clientId)
            .then(res => setFeedbackRequests(prev => ({ ...prev, [clientId]: res.data })))
            .catch(err => console.error('Error loading feedback requests:', err));
    };

    const toggleExpandClient = (clientId) => {
        if (expandedClientId === clientId) {
            setExpandedClientId(null);
            setShowAddContact(false);
            setContactForm(BLANK_CONTACT);
            setEditingContact(null);
        } else {
            setExpandedClientId(clientId);
            setShowAddContact(false);
            setContactForm(BLANK_CONTACT);
            setEditingContact(null);
            loadFeedbackRequests(clientId);
        }
    };

    // ── Contacts ─────────────────────────────────────────────
    const handleAddContact = async (clientId) => {
        if (!contactForm.first_name.trim() || !contactForm.last_name.trim()) return;
        try {
            await API.createTeamClientContact(selectedTeam.team_id, clientId, {
                ...contactForm,
                user_id: auth.user.user_id,
            });
            setContactForm(BLANK_CONTACT);
            setShowAddContact(false);
            fetchClients(selectedTeam.team_id);
        } catch (err) {
            console.error("Error adding contact:", err);
        }
    };

    const handleSaveContact = async (clientId, contactId) => {
        if (!editingContact.form.first_name.trim() || !editingContact.form.last_name.trim()) return;
        try {
            await API.updateTeamClientContact(selectedTeam.team_id, clientId, contactId, {
                ...editingContact.form,
                user_id: auth.user.user_id,
            });
            setEditingContact(null);
            fetchClients(selectedTeam.team_id);
        } catch (err) {
            console.error("Error updating contact:", err);
        }
    };

    const handleDeleteContact = async (clientId, contactId) => {
        if (!window.confirm("Remove this contact?")) return;
        try {
            await API.deleteTeamClientContact(selectedTeam.team_id, clientId, contactId, auth.user.user_id);
            fetchClients(selectedTeam.team_id);
        } catch (err) {
            console.error("Error deleting contact:", err);
        }
    };

    // ── Bill Work ────────────────────────────────────────────
    const fetchTrackedBillsOverview = async (teamId) => {
        setBillWorkLoading(true);
        try {
            const [overviewRes, tagsRes] = await Promise.all([
                API.getTrackedBillsOverview(teamId, auth.user.user_id),
                API.getTeamSubjectTags(teamId, auth.user.user_id),
            ]);
            setTrackedBillsOverview(overviewRes.data);
            setTeamSubjectTags(tagsRes.data);
        } catch (err) {
            console.error('Error fetching bill overview:', err);
        } finally {
            setBillWorkLoading(false);
        }
    };

    const fetchReports = async (teamId) => {
        setReportsLoading(true);
        try {
            const res = await API.getTeamReports(teamId);
            setReports(res.data);
        } catch (err) {
            console.error('Error fetching reports:', err);
        } finally {
            setReportsLoading(false);
        }
    };

    const fetchAmbassadorOverview = async (teamId) => {
        setAmbassadorLoading(true);
        try {
            const res = await API.getTeamAmbassadorOverview(teamId, { user_id: auth.user.user_id });
            setAmbassadorOverview(res.data);
        } catch (err) {
            console.error('Error fetching ambassador overview:', err);
        } finally {
            setAmbassadorLoading(false);
        }
    };

    const fetchMyAmbassadorInfo = async () => {
        try {
            const res = await API.getMyAmbassadorInfo();
            setMyAmbassadorInfo(res.data);
        } catch (err) {
            if (err.response?.status !== 404) {
                console.error('Error fetching ambassador info:', err);
            }
            setMyAmbassadorInfo(null);
        }
    };

    const handleTeamTabChange = (tab) => {
        setTeamTab(tab);
        if (tab === 'billwork' && trackedBillsOverview.length === 0 && !billWorkLoading) {
            fetchTrackedBillsOverview(selectedTeam.team_id);
        }
        if (tab === 'tasks') {
            fetchTeamTasks(taskDashFilter);
        }
        if (tab === 'reports' && reports.length === 0 && !reportsLoading) {
            fetchReports(selectedTeam.team_id);
        }
        if (tab === 'rulemaking' && !teamRulemaking && !teamRulemakingLoading) {
            fetchTeamRulemaking(selectedTeam.team_id);
        }
        if (tab === 'savedfilters' && teamSavedFilters.length === 0 && !teamSavedFiltersLoading) {
            fetchTeamSavedFilters(selectedTeam.team_id);
        }
        if (tab === 'ambassador' && !ambassadorOverview && !ambassadorLoading) {
            fetchAmbassadorOverview(selectedTeam.team_id);
        }
    };

    const handleBillExpand = async (billId) => {
        if (expandedBillId === billId) {
            setExpandedBillId(null);
            return;
        }
        setExpandedBillId(billId);
        if (!billTasks[billId]) {
            try {
                const [tasksRes, notesRes] = await Promise.all([
                    API.getBillTasks(selectedTeam.team_id, billId, auth.user.user_id),
                    API.getBillNotes(selectedTeam.team_id, billId, auth.user.user_id),
                ]);
                setBillTasks(prev => ({ ...prev, [billId]: tasksRes.data }));
                setBillNotes(prev => ({ ...prev, [billId]: notesRes.data }));
            } catch (err) {
                console.error('Error loading bill work:', err);
            }
        }
    };

    const refreshBillOverview = async () => {
        if (!selectedTeam) return;
        try {
            const res = await API.getTrackedBillsOverview(selectedTeam.team_id, auth.user.user_id);
            setTrackedBillsOverview(res.data);
        } catch (err) { /* silent */ }
    };

    const handleAddAssignment = async (billId) => {
        const form = addAssignForm[billId];
        if (!form?.userId) return;
        try {
            await API.addBillAssignment(selectedTeam.team_id, billId, {
                user_id: form.userId,
                role: form.role || 'Monitor',
                requesting_user_id: auth.user.user_id,
            });
            setAddAssignForm(prev => ({ ...prev, [billId]: { userId: '', role: 'Monitor' } }));
            await refreshBillOverview();
        } catch (err) {
            alert(err.response?.data?.message || 'Error adding assignment.');
        }
    };

    const handleRoleChange = async (billId, assignId, newRole) => {
        try {
            await API.updateBillAssignment(selectedTeam.team_id, billId, assignId, {
                role: newRole,
                requesting_user_id: auth.user.user_id,
            });
            setTrackedBillsOverview(prev => prev.map(b => {
                if (b.bill_id !== billId) return b;
                return { ...b, assignments: b.assignments.map(a => a.id === assignId ? { ...a, role: newRole } : a) };
            }));
        } catch (err) {
            alert(err.response?.data?.message || 'Error updating role.');
        }
    };

    const handleRemoveAssignment = async (billId, assignId) => {
        try {
            await API.removeBillAssignment(selectedTeam.team_id, billId, assignId, auth.user.user_id);
            setTrackedBillsOverview(prev => prev.map(b => {
                if (b.bill_id !== billId) return b;
                return { ...b, assignments: b.assignments.filter(a => a.id !== assignId) };
            }));
        } catch (err) {
            alert('Error removing assignment.');
        }
    };

    const handleBulkReassign = async () => {
        if (!bulkFrom || !bulkTo) return;
        try {
            const res = await API.bulkReassign(selectedTeam.team_id, {
                from_user_id: bulkFrom,
                to_user_id: bulkTo,
                requesting_user_id: auth.user.user_id,
            });
            setBulkResult(res.data);
            setBulkFrom('');
            setBulkTo('');
            await refreshBillOverview();
        } catch (err) {
            alert(err.response?.data?.message || 'Error during bulk reassign.');
        }
    };

    // ── Tasks ────────────────────────────────────────────────
    const fetchTeamTasks = async (filter) => {
        if (!selectedTeam) return;
        setTeamTasksLoading(true);
        try {
            const res = await API.getTeamTasks(selectedTeam.team_id, auth.user.user_id, filter);
            setTeamTasks(res.data);
        } catch (err) {
            console.error('Error fetching team tasks:', err);
        } finally {
            setTeamTasksLoading(false);
        }
    };

    const handleAddTask = async (billId) => {
        const text = newTaskText[billId]?.trim();
        if (!text) return;
        try {
            const res = await API.createBillTask(selectedTeam.team_id, billId, {
                task_text: text,
                assigned_to_user_id: newTaskAssignee[billId] || null,
                due_date: newTaskDue[billId] || null,
                priority: newTaskPriority[billId] || null,
                created_by_user_id: auth.user.user_id,
            });
            setBillTasks(prev => ({ ...prev, [billId]: [...(prev[billId] || []), res.data] }));
            setNewTaskText(prev => ({ ...prev, [billId]: '' }));
            setNewTaskAssignee(prev => ({ ...prev, [billId]: '' }));
            setNewTaskDue(prev => ({ ...prev, [billId]: '' }));
            setNewTaskPriority(prev => ({ ...prev, [billId]: '' }));
            setTrackedBillsOverview(prev => prev.map(b =>
                b.bill_id === billId ? { ...b, open_task_count: b.open_task_count + 1 } : b
            ));
        } catch (err) {
            alert('Error adding task.');
        }
    };

    // A task with no billId belongs to a rulemaking item or is a general task, and goes through the
    // bill-less task API.
    const handleUpdateTask = async (billId, taskId, updates) => {
        if (billId) {
            setBillTasks(prev => ({
                ...prev,
                [billId]: (prev[billId] || []).map(t => t.id === taskId ? { ...t, ...updates } : t),
            }));
        }
        setTeamTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updates } : t));
        if (!billId) {
            try {
                const res = await API.updateTeamTask(selectedTeam.team_id, taskId, { ...updates, requesting_user_id: auth.user.user_id });
                if (res.data.rulemaking_ref) setTeamRulemakingTasks(prev => prev.map(t => t.id === taskId ? res.data : t));
            } catch (err) {
                alert(err.response?.data?.message || 'Error updating task.');
                fetchTeamTasks(taskDashFilter);
            }
            return;
        }
        try {
            await API.updateBillTask(selectedTeam.team_id, billId, taskId, {
                ...updates,
                requesting_user_id: auth.user.user_id,
            });
        } catch (err) {
            console.error('Error updating task:', err);
        }
    };

    // Fields saved from the task detail modal (it makes its own API calls), mirrored into every list holding the task
    const applyTaskFields = (task, fields) => {
        const merge = t => t.id === task.id ? { ...t, ...fields } : t;
        if (task.bill_id) {
            setBillTasks(prev => prev[task.bill_id] ? { ...prev, [task.bill_id]: prev[task.bill_id].map(merge) } : prev);
        }
        if (task.rulemaking_ref) setTeamRulemakingTasks(prev => prev.map(merge));
        setTeamTasks(prev => prev.map(merge));
    };

    const handleToggleTask = async (billId, task) => {
        const nowComplete = !task.completed;
        setBillTasks(prev => ({
            ...prev,
            [billId]: prev[billId].map(t => t.id === task.id ? { ...t, completed: nowComplete } : t),
        }));
        setTrackedBillsOverview(prev => prev.map(b => {
            if (b.bill_id !== billId) return b;
            return { ...b, open_task_count: b.open_task_count + (nowComplete ? -1 : 1) };
        }));
        try {
            await API.updateBillTask(selectedTeam.team_id, billId, task.id, {
                completed: nowComplete,
                requesting_user_id: auth.user.user_id,
            });
        } catch (err) {
            // revert on failure
            setBillTasks(prev => ({
                ...prev,
                [billId]: prev[billId].map(t => t.id === task.id ? { ...t, completed: task.completed } : t),
            }));
        }
    };

    const handleDeleteTask = async (billId, taskId) => {
        const wasOpen = billTasks[billId]?.find(t => t.id === taskId)?.completed === false;
        setBillTasks(prev => ({ ...prev, [billId]: prev[billId].filter(t => t.id !== taskId) }));
        if (wasOpen) {
            setTrackedBillsOverview(prev => prev.map(b =>
                b.bill_id === billId ? { ...b, open_task_count: Math.max(0, b.open_task_count - 1) } : b
            ));
        }
        try {
            await API.deleteBillTask(selectedTeam.team_id, billId, taskId, auth.user.user_id);
        } catch (err) {
            alert('Error deleting task.');
        }
    };

    const handleAddNote = async (billId) => {
        const text = newNoteText[billId]?.trim();
        if (!text) return;
        try {
            const res = await API.createBillNote(selectedTeam.team_id, billId, {
                note_text: text,
                created_by_user_id: auth.user.user_id,
            });
            const authorMember = selectedTeam.TeamMembers.find(m => m.user_id === auth.user.user_id);
            const enriched = {
                ...res.data,
                Author: authorMember
                    ? { first_name: authorMember.ProfileInfo.first_name, last_name: authorMember.ProfileInfo.last_name }
                    : null,
            };
            setBillNotes(prev => ({ ...prev, [billId]: [enriched, ...(prev[billId] || [])] }));
            setNewNoteText(prev => ({ ...prev, [billId]: '' }));
        } catch (err) {
            alert('Error adding note.');
        }
    };

    const handleDeleteNote = async (billId, noteId) => {
        setBillNotes(prev => ({ ...prev, [billId]: prev[billId].filter(n => n.id !== noteId) }));
        try {
            await API.deleteBillNote(selectedTeam.team_id, billId, noteId, auth.user.user_id);
        } catch (err) {
            alert('Error deleting note.');
        }
    };

    // ── Subject Tags ─────────────────────────────────────────
    const handleCreateSubjectTag = async (e) => {
        e.preventDefault();
        const name = newTagName.trim();
        if (!name) return;
        try {
            const res = await API.createTeamSubjectTag(selectedTeam.team_id, {
                name,
                requesting_user_id: auth.user.user_id,
            });
            setTeamSubjectTags(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
            setNewTagName('');
        } catch (err) {
            alert(err.response?.data?.message || 'Error creating tag.');
        }
    };

    const handleDeleteSubjectTagDef = async (tagId) => {
        setTeamSubjectTags(prev => prev.filter(t => t.tag_id !== tagId));
        setTrackedBillsOverview(prev => prev.map(b => ({
            ...b,
            subjectTags: b.subjectTags.filter(t => t.tag_id !== tagId),
        })));
        if (billSubjectTagFilter === String(tagId)) setBillSubjectTagFilter('');
        try {
            await API.deleteTeamSubjectTag(selectedTeam.team_id, tagId, auth.user.user_id);
        } catch (err) {
            alert('Error deleting tag.');
        }
    };

    const handleAddSubjectTagToBill = async (billId) => {
        const tagId = addTagSelect[billId];
        if (!tagId) return;
        const tag = teamSubjectTags.find(t => String(t.tag_id) === String(tagId));
        if (!tag) return;
        setTrackedBillsOverview(prev => prev.map(b =>
            b.bill_id === billId ? { ...b, subjectTags: [...b.subjectTags, tag] } : b
        ));
        setAddTagSelect(prev => ({ ...prev, [billId]: '' }));
        try {
            await API.addBillSubjectTag(selectedTeam.team_id, billId, {
                tag_id: tag.tag_id,
                requesting_user_id: auth.user.user_id,
            });
        } catch (err) {
            setTrackedBillsOverview(prev => prev.map(b =>
                b.bill_id === billId ? { ...b, subjectTags: b.subjectTags.filter(t => t.tag_id !== tag.tag_id) } : b
            ));
            alert('Error adding tag.');
        }
    };

    const handleRemoveSubjectTagFromBill = async (billId, tagId) => {
        setTrackedBillsOverview(prev => prev.map(b =>
            b.bill_id === billId ? { ...b, subjectTags: b.subjectTags.filter(t => t.tag_id !== tagId) } : b
        ));
        try {
            await API.removeBillSubjectTag(selectedTeam.team_id, billId, tagId, auth.user.user_id);
        } catch (err) {
            alert('Error removing tag.');
        }
    };

    const handleCreateTeam = async (e) => {
        e.preventDefault();
        setCreateError("");
        try {
            await API.createTeam({
                team_name: newTeamName,
                user_id: auth.user.user_id
            });
            setNewTeamName("");
            setIsCreating(false);
            fetchTeams();
        } catch (err) {
            const msg = err.response?.data?.message || "Error creating workspace.";
            setCreateError(msg);
        }
    };

    const viewTeamDetails = async (teamId) => {
        try {
            const res = await API.getTeamDetails(teamId);
            setShowAboutWorkspaces(false);
            setSelectedTeam(res.data);
            storeSelectedTeamId(auth.user.user_id, teamId);
            setEligibleMembers([]);
            setSearchTerm('');
            setSearchResults([]);
            if (res.data.organization_id) {
                API.getEligibleMembers(teamId)
                    .then(r => setEligibleMembers(r.data))
                    .catch(() => {});
            }
        } catch (err) {
            console.error("Error fetching team details:", err);
        }
    };

    const handleSearchUser = async (val) => {
        setSearchTerm(val);
        if (val.length > 2) {
            const res = await API.searchTeamMembers(selectedTeam.team_id, val);
            setSearchResults(res.data);
        } else {
            setSearchResults([]);
        }
    };

    const addMember = async (profile) => {
        try {
            await API.addTeamMember(selectedTeam.team_id, {
                user_id: profile.UserAccount?.user_id || profile.user_id,
                profile_id: profile.profile_id
            });
            setSearchTerm("");
            setSearchResults([]);
            viewTeamDetails(selectedTeam.team_id);
        } catch (err) {
            alert(err.response?.data?.message || "Error adding member.");
        }
    };

    const handleTileClick = (profile) => {
        if (profile.status === 'ready') {
            addMember(profile);
        } else {
            const type = profile.status === 'unsubscribed' ? 'subscribe' : 'register';
            setPendingInvite({ profile, type });
        }
    };

    const sendInvite = async () => {
        if (!pendingInvite) return;
        const { profile, type } = pendingInvite;
        try {
            await API.inviteTeamMember(selectedTeam.team_id, {
                sender_user_id: auth.user.user_id,
                profile_id: profile.profile_id,
                invite_type: type,
            });
            setInviteSent(prev => ({ ...prev, [profile.profile_id]: true }));
            setPendingInvite(null);
        } catch (err) {
            alert(err.response?.data?.msg || 'Error sending invite.');
        }
    };

    const handleEditNameStart = () => {
        setEditedTeamName(selectedTeam.team_name);
        setEditedTeamAcronym(selectedTeam.team_acronym || "");
        setEditedTagColor(selectedTeam.tag_color || "");
        setIsEditingName(true);
    };

    const handleSaveTeamName = async () => {
        if (!editedTeamName.trim()) return;
        try {
            await API.updateTeamName(selectedTeam.team_id, {
                team_name: editedTeamName.trim(),
                team_acronym: editedTeamAcronym.trim().toUpperCase(),
                tag_color: editedTagColor || null,
                user_id: auth.user.user_id
            });
            setIsEditingName(false);
            await fetchTeams();
            viewTeamDetails(selectedTeam.team_id);
        } catch (err) {
            console.error("Error updating team name:", err);
        }
    };

    const handleDeleteTeam = async () => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${selectedTeam.team_name}"?\n\nThis action is permanent. All members, tracked bills, and associated data for this team will also be deleted and cannot be recovered.`
        );
        if (!confirmed) return;
        try {
            await API.deleteTeam(selectedTeam.team_id, auth.user.user_id);
            setSelectedTeam(null);
            fetchTeams();
        } catch (err) {
            console.error("Error deleting team:", err);
        }
    };

    const updateMember = async (memberId, field, value) => {
        try {
            const member = selectedTeam.TeamMembers.find(m => m.member_id === memberId);
            const updatedData = {
                account_manage: field === 'account_manage' ? value : member.account_manage,
                bill_manage: field === 'bill_manage' ? value : member.bill_manage,
                view_only: field === 'view_only' ? value : member.view_only
            };
            
            // Ensure view_only is false if a manage flag is true
            if ((updatedData.account_manage || updatedData.bill_manage)) {
                updatedData.view_only = false;
            }

            await API.updateMemberPermissions(selectedTeam.team_id, memberId, updatedData);
            viewTeamDetails(selectedTeam.team_id);
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) return <div className="text-center mt-5">Loading teams...</div>;

    const currentMembership = selectedTeam?.TeamMembers?.find(m => m.user_id === auth.user.user_id);
    const canManage = selectedTeam?.team_admin === auth.user.user_id || currentMembership?.account_manage === true;
    const canBill = canManage || currentMembership?.bill_manage === true;
    const isAlreadyTeamAdmin = myTeams.some(m => m.TeamAccount?.team_admin === auth.user.user_id);
    const canCreateWorkspace = workplaceSubscriber && !isAlreadyTeamAdmin;

    return (
        <div className="container teams-container">
            <PageTitle title={selectedTeam?.team_name ? `${selectedTeam.team_name} | 'elloGovna` : "Workspaces | 'elloGovna"} />
            <div className="row">
                <div className="col-md-3 border-end">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="teams-sidebar-title">My Workspaces</h4>
                        {canCreateWorkspace && (
                            <button className="btn btn-sm btn-outline-primary me-lg-2" onClick={() => setIsCreating(!isCreating)}>
                                {isCreating ? "Cancel" : "New Workspace"}
                            </button>
                        )}
                    </div>

                    {!workplaceSubscriber && (
                        <p className="text-muted small mb-3">
                            <a href="/subscribe?plan=workplace">Subscribe to a Workspace plan</a> to create your own Workspace.
                        </p>
                    )}

                    {canCreateWorkspace && isCreating && (
                        <form onSubmit={handleCreateTeam} className="mb-4">
                            <input
                                className="form-control mb-2"
                                placeholder="Workspace Name"
                                value={newTeamName}
                                onChange={(e) => { setNewTeamName(e.target.value); setCreateError(""); }}
                                required
                            />
                            {createError && (
                                <p className="text-danger small mb-2">{createError}</p>
                            )}
                            <button type="submit" className="btn btn-primary w-40">Create</button>
                        </form>
                    )}

                    <div className="list-group list-group-flush me-lg-2">
                        {myTeams.map(membership => (
                            <button 
                                key={membership.team_id} 
                                className={`list-group-item list-group-item-action ${selectedTeam?.team_id === membership.team_id && !showAboutWorkspaces ? 'active' : ''}`}
                                onClick={() => viewTeamDetails(membership.team_id)}
                            >
                                {membership.TeamAccount.team_name}
                                <div className="small opacity-75">
                                    Role: {membership.account_manage ? "Admin" : membership.bill_manage ? "Editor" : "Viewer"}
                                </div>
                            </button>
                        ))}
                    </div>

                    <button
                        type="button"
                        className={`btn btn-link btn-sm teams-about-trigger p-0 mt-3${showAboutWorkspaces ? ' teams-about-trigger--active' : ''}`}
                        onClick={() => { setShowAboutWorkspaces(true); setSelectedTeam(null); }}
                    >
                        <i className="bi bi-info-circle me-1" />About Workspaces
                    </button>
                </div>

                <div className="col-md-9 px-4">
                    {showAboutWorkspaces ? (
                        <div className="teams-about">
                            <h2 className="teams-main-title mb-2">About Workspaces</h2>
                            <p className="text-muted mb-4">
                                A Workspace is a shared account for your team to track bills together, assign work,
                                collect feedback from Clients or Stakeholder Groups, and build reports — all in one
                                place. What a member can do inside a Workspace depends on their access level below.
                            </p>

                            <h5 className="teams-about-section-title">Access Levels</h5>
                            <ul className="teams-about-access-list">
                                {WORKSPACE_ACCESS_LEVELS.map(level => (
                                    <li key={level.key} className="teams-about-access-item">
                                        <div className="teams-about-access-header">
                                            <i className={`bi ${level.icon} teams-about-access-icon`} />
                                            <span className="teams-about-access-label">{level.label}</span>
                                        </div>
                                        <p className="teams-about-access-summary">{level.summary}</p>
                                        <ul className="teams-about-can-list">
                                            {level.can.map((c, i) => <li key={i}>{c}</li>)}
                                        </ul>
                                    </li>
                                ))}
                            </ul>

                            <h5 className="teams-about-section-title">Features</h5>
                            <ul className="teams-about-feature-list mb-4">
                                {WORKSPACE_FEATURES.map(f => (
                                    <li key={f.key} className="teams-about-feature-item">
                                        <i className={`bi ${f.icon} teams-about-feature-icon`} />
                                        <div>
                                            <div className="teams-about-feature-label">{f.label}</div>
                                            <p className="teams-about-feature-desc">{f.desc}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : selectedTeam ? (
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-4">
                                {isEditingName ? (
                                    <>
                                        <div className="d-flex flex-column gap-1 flex-grow-1">
                                            <input
                                                className="form-control form-control-lg teams-name-input"
                                                value={editedTeamName}
                                                onChange={(e) => setEditedTeamName(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTeamName(); if (e.key === 'Escape') setIsEditingName(false); }}
                                                autoFocus
                                                placeholder="Team name"
                                            />
                                            <div className="d-flex align-items-center gap-2">
                                                <input
                                                    className="form-control form-control-sm teams-acronym-input"
                                                    value={editedTeamAcronym}
                                                    onChange={(e) => setEditedTeamAcronym(e.target.value.slice(0, 10))}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTeamName(); if (e.key === 'Escape') setIsEditingName(false); }}
                                                    placeholder="Acronym (optional, max 10 chars)"
                                                    style={{ maxWidth: '240px' }}
                                                />
                                            </div>
                                            <div className="d-flex align-items-center gap-2 mt-1">
                                                <span className="teams-color-label">Tag color:</span>
                                                {TAG_COLOR_OPTIONS.map(opt => (
                                                    <button
                                                        key={opt.key}
                                                        type="button"
                                                        title={opt.label}
                                                        className={`teams-color-swatch${editedTagColor === opt.key ? ' teams-color-swatch--active' : ''}`}
                                                        style={{ background: opt.bg, color: opt.text }}
                                                        onClick={() => setEditedTagColor(editedTagColor === opt.key ? '' : opt.key)}
                                                    >
                                                        {editedTagColor === opt.key && <i className="bi bi-check2" />}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <button className="btn btn-sm btn-primary" onClick={handleSaveTeamName}>Save</button>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setIsEditingName(false)}>Cancel</button>
                                    </>
                                ) : (
                                    <>
                                        <div className="d-flex flex-column">
                                            <h2 className="teams-main-title mb-0">{selectedTeam.team_name}</h2>
                                            {(selectedTeam.team_acronym || selectedTeam.tag_color) && (
                                                <div className="d-flex align-items-center gap-2 mt-1">
                                                    {selectedTeam.team_acronym && (
                                                        <span className="teams-acronym-display">{selectedTeam.team_acronym}</span>
                                                    )}
                                                    {selectedTeam.tag_color && (() => {
                                                        const opt = TAG_COLOR_OPTIONS.find(o => o.key === selectedTeam.tag_color);
                                                        return opt ? (
                                                            <span className="hearing-track-tag" style={{ background: opt.bg, color: opt.text }}>
                                                                {selectedTeam.team_acronym || selectedTeam.team_name.split(/\s+/).map(w => w[0]).join('').toUpperCase()}
                                                            </span>
                                                        ) : null;
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                        {selectedTeam.team_admin === auth.user.user_id && (
                                            <i className="bi bi-pencil teams-action-icon" title="Rename team" onClick={handleEditNameStart}></i>
                                        )}
                                        <button className="btn btn-sm teams-feedback-new-btn ms-auto" onClick={() => setShowAddTask(true)}>
                                            <i className="bi bi-plus-lg me-1"></i>Add Task
                                        </button>
                                        {selectedTeam.team_admin === auth.user.user_id && (
                                            <i className="bi bi-trash teams-action-icon teams-delete-icon" title={`Delete ${selectedTeam.team_name}`} onClick={handleDeleteTeam}></i>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* ── Section tabs ── */}
                            <div className="tw-detail-tabs mb-4">
                                <button className={`tw-detail-tab${teamTab === 'overview' ? ' active' : ''}`} onClick={() => handleTeamTabChange('overview')}>Overview</button>
                                <button className={`tw-detail-tab${teamTab === 'billwork' ? ' active' : ''}`} onClick={() => handleTeamTabChange('billwork')}>
                                    Bill Work
                                    {trackedBillsOverview.reduce((s, b) => s + b.open_task_count, 0) > 0 && (
                                        <span className="tw-tab-badge">{trackedBillsOverview.reduce((s, b) => s + b.open_task_count, 0)}</span>
                                    )}
                                </button>
                                <button className={`tw-detail-tab${teamTab === 'tasks' ? ' active' : ''}`} onClick={() => handleTeamTabChange('tasks')}>All Tasks</button>
                                <button className={`tw-detail-tab${teamTab === 'reports' ? ' active' : ''}`} onClick={() => handleTeamTabChange('reports')}>Reports</button>
                                <button className={`tw-detail-tab${teamTab === 'rulemaking' ? ' active' : ''}`} onClick={() => handleTeamTabChange('rulemaking')}>Rulemaking</button>
                                <button className={`tw-detail-tab${teamTab === 'savedfilters' ? ' active' : ''}`} onClick={() => handleTeamTabChange('savedfilters')}>Saved Filters</button>
                                {selectedTeam.ambassador_enabled && canManage && (
                                    <button className={`tw-detail-tab${teamTab === 'ambassador' ? ' active' : ''}`} onClick={() => handleTeamTabChange('ambassador')}>Ambassador Results</button>
                                )}
                            </div>

                            {teamTab === 'overview' && (<>
                            {selectedTeam.ambassador_enabled && myAmbassadorInfo?.referral_token && (
                                <div className="alert alert-info d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                                    <span>
                                        <i className="bi bi-megaphone me-2"></i>
                                        Your referral link: <strong>{`${window.location.origin}/join/${myAmbassadorInfo.referral_token}`}</strong>
                                    </span>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => navigator.clipboard.writeText(`${window.location.origin}/join/${myAmbassadorInfo.referral_token}`)}
                                    >
                                        <i className="bi bi-copy me-1"></i>Copy
                                    </button>
                                </div>
                            )}
                            <h5 className="mb-3">Members</h5>
                            <table className="table table-sm align-middle">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Account Manage</th>
                                        <th>Bill Manage</th>
                                        <th>View Only</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedTeam.TeamMembers.map(member => (
                                        <tr key={member.member_id}>
                                            <td>
                                                {member.ProfileInfo?.first_name || member.UserAccount?.first_name} {member.ProfileInfo?.last_name || member.UserAccount?.last_name}
                                                {selectedTeam.team_admin === member.user_id && <span className="badge bg-info ms-2">Owner</span>}
                                                {member.status === 'pending' && <span className="badge bg-warning text-dark ms-2">Pending</span>}
                                            </td>
                                            <td>
                                                <input type="checkbox" checked={member.account_manage}
                                                    disabled={!canManage || selectedTeam.team_admin === member.user_id}
                                                    onChange={(e) => updateMember(member.member_id, 'account_manage', e.target.checked)} />
                                            </td>
                                            <td>
                                                <input type="checkbox" checked={member.bill_manage}
                                                    disabled={!canManage || selectedTeam.team_admin === member.user_id}
                                                    onChange={(e) => updateMember(member.member_id, 'bill_manage', e.target.checked)} />
                                            </td>
                                            <td>
                                                <input type="checkbox" checked={member.view_only}
                                                    disabled={!canManage || selectedTeam.team_admin === member.user_id || member.account_manage || member.bill_manage}
                                                    onChange={(e) => updateMember(member.member_id, 'view_only', e.target.checked)} />
                                            </td>
                                            <td>
                                                {canManage && selectedTeam.team_admin !== member.user_id && (
                                                    <i className="bi bi-trash text-danger pointer" onClick={() => API.removeTeamMember(selectedTeam.team_id, member.member_id).then(() => viewTeamDetails(selectedTeam.team_id))} />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {canManage && (
                            <div className="mt-3 pt-3">
                                <h6>Add New Member</h6>
                                {selectedTeam.organization_id ? (
                                    <>
                                        {eligibleMembers.length > 0 && (
                                            <div className="teams-eligible-grid">
                                                {eligibleMembers.map(p => {
                                                    const sent = inviteSent[p.profile_id];
                                                    const tileClass = `teams-eligible-tile teams-eligible-tile--${p.status}${sent ? ' teams-eligible-tile--sent' : ''}`;
                                                    const title = p.status === 'ready'
                                                        ? `Add ${p.first_name} ${p.last_name}`
                                                        : p.status === 'unsubscribed'
                                                        ? `Invite ${p.first_name} to subscribe`
                                                        : `Invite ${p.first_name} to register`;
                                                    return (
                                                        <button
                                                            key={p.profile_id}
                                                            className={tileClass}
                                                            onClick={() => !sent && handleTileClick(p)}
                                                            title={sent ? 'Invite sent' : title}
                                                            disabled={sent}
                                                        >
                                                            {p.profile_picture
                                                                ? <img src={p.profile_picture} alt="" className="teams-eligible-photo ms-1" />
                                                                : <div className="teams-eligible-initials">
                                                                    {p.first_name?.[0]}{p.last_name?.[0]}
                                                                  </div>
                                                            }
                                                            <div className="teams-eligible-info">
                                                                <span className="teams-eligible-name">{p.first_name} {p.last_name}</span>
                                                                {p.JobExperiences?.[0]?.title && (
                                                                    <span className="teams-eligible-title">{p.JobExperiences[0].title}</span>
                                                                )}
                                                                {p.status !== 'ready' && (
                                                                    <span className="teams-eligible-status-tag">
                                                                        {sent ? 'Invite sent' : p.status === 'unsubscribed' ? 'No subscription' : 'No account'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Invite confirmation */}
                                        {pendingInvite && (
                                            <div className="teams-invite-confirm">
                                                <p className="teams-invite-confirm-msg">
                                                    {pendingInvite.type === 'subscribe'
                                                        ? <>Send <strong>{pendingInvite.profile.first_name} {pendingInvite.profile.last_name}</strong> an email encouraging them to subscribe so they can be added to this workspace?</>
                                                        : <>Send <strong>{pendingInvite.profile.first_name} {pendingInvite.profile.last_name}</strong> an email encouraging them to create an account and subscribe?</>
                                                    }
                                                </p>
                                                <div className="d-flex gap-2">
                                                    <button className="btn btn-sm btn-primary" onClick={sendInvite}>Send Invite</button>
                                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingInvite(null)}>Cancel</button>
                                                </div>
                                            </div>
                                        )}
                                        <div className="position-relative mt-2">
                                            <input
                                                className="form-control form-control-sm"
                                                placeholder="Search for others by name…"
                                                value={searchTerm}
                                                onChange={(e) => handleSearchUser(e.target.value)}
                                            />
                                            {searchResults.length > 0 && (
                                                <div className="list-group position-absolute w-100 z-3 shadow">
                                                    {searchResults
                                                        .filter(p => !eligibleMembers.find(e => e.user_id === p.user_id))
                                                        .map(p => (
                                                            <button
                                                                key={p.user_id}
                                                                className="list-group-item list-group-item-action"
                                                                onClick={() => addMember(p)}
                                                            >
                                                                <strong>{p.first_name} {p.last_name}</strong>
                                                                {p.title && <>, {p.title}</>}
                                                                {p.employer && <> at {p.employer}</>}
                                                                {p.email && <div className="text-muted small">{p.email}</div>}
                                                            </button>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-muted small mb-0">Link an organization to this workspace (below) before adding members.</p>
                                )}
                            </div>
                            )}

                            {/* ── Organization Association ── */}
                            <div className="mt-5 pt-3 border-top">
                                <h5 className="mb-2">Linked Organization</h5>
                                {selectedTeam.Organization && !isChangingOrg ? (
                                    <div className="d-flex align-items-center gap-2 teams-org-row">
                                        <span className="teams-org-name">{selectedTeam.Organization.organization_name}</span>
                                        {selectedTeam.Organization.group_type && (
                                            <span className="badge teams-org-type-badge">{selectedTeam.Organization.group_type}</span>
                                        )}
                                        {isOwner && (
                                            <>
                                                <button
                                                    className="btn btn-sm teams-org-action-btn ms-2"
                                                    onClick={async () => {
                                                        const res = await API.getSingleOrganizations(selectedTeam.Organization.organization_id);
                                                        setEditOrgData(res.data);
                                                    }}
                                                    title="Edit organization details"
                                                >
                                                    Edit
                                                </button>
                                                <button className="btn btn-sm teams-org-action-btn" onClick={() => { setIsChangingOrg(true); setOrgQuery(""); setOrgResults([]); }} title="Change organization">
                                                    Change
                                                </button>
                                                <button className="btn btn-sm teams-org-clear-btn" onClick={handleClearOrg} title="Remove association">
                                                    <i className="bi bi-x-lg"></i>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                ) : isOwner ? (
                                    <div ref={orgSearchRef} className="position-relative teams-org-search-wrap">
                                        {isChangingOrg && (
                                            <p className="small text-muted mb-2">
                                                Selecting an organization will replace the current one.
                                            </p>
                                        )}
                                        <div className="d-flex gap-2 align-items-center">
                                            <input
                                                className="form-control"
                                                placeholder="Search for an organization…"
                                                value={orgQuery}
                                                onChange={(e) => handleOrgSearch(e.target.value)}
                                                onFocus={() => orgResults.length > 0 && setOrgSearchOpen(true)}
                                                autoFocus={isChangingOrg}
                                            />
                                            {isChangingOrg && (
                                                <button className="btn btn-sm btn-outline-secondary text-nowrap" onClick={() => { setIsChangingOrg(false); setOrgQuery(""); setOrgResults([]); }}>
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                        {orgSearchOpen && orgResults.length > 0 && (
                                            <div className="list-group position-absolute w-100 z-3 shadow">
                                                {orgResults.map(org => (
                                                    <button
                                                        key={org.organization_id}
                                                        className="list-group-item list-group-item-action"
                                                        onClick={() => handleSelectOrg(org)}
                                                    >
                                                        {org.organization_name}
                                                        {org.group_type && <span className="ms-2 badge teams-org-type-badge">{org.group_type}</span>}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-muted fst-italic small mb-0">No organization associated.</p>
                                )}
                            </div>

                            {/* ── Clients / Stakeholder Groups ── */}
                            <div className="mt-5 pt-3 border-top">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <h5 className="mb-0">{sectionLabel}</h5>
                                        {canBill && clients.length > 0 && (
                                            <button
                                                className="btn btn-sm teams-feedback-new-btn"
                                                onClick={() => setShowFeedbackModal(true)}
                                            >
                                                <i className="bi bi-send me-1" />New Feedback Request
                                            </button>
                                        )}
                                    </div>

                                    {clients.filter(c => !c.archived).length === 0 && (
                                        <p className="text-muted fst-italic small">No {sectionLabel.toLowerCase()} added yet.</p>
                                    )}

                                    <div className="teams-clients-list">
                                        {clients.filter(c => !c.archived).map(client => (
                                            <div key={client.client_id} className="teams-client-card">
                                                {/* Client header row */}
                                                <div className="teams-client-header" onClick={() => toggleExpandClient(client.client_id)}>
                                                    {editingClientId === client.client_id ? (
                                                        <div className="d-flex align-items-center gap-2 flex-grow-1" onClick={e => e.stopPropagation()}>
                                                            <input
                                                                className="form-control form-control-sm teams-client-name-input"
                                                                value={editingClientName}
                                                                onChange={e => setEditingClientName(e.target.value)}
                                                                onKeyDown={e => { if (e.key === 'Enter') handleSaveClientName(client.client_id); if (e.key === 'Escape') setEditingClientId(null); }}
                                                                autoFocus
                                                            />
                                                            <button className="btn btn-sm btn-primary" onClick={() => handleSaveClientName(client.client_id)}>Save</button>
                                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingClientId(null)}>Cancel</button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <span className="teams-client-name">{client.client_name}</span>
                                                            <span className="teams-client-contact-count ms-2 text-muted small">
                                                                {client.TeamClientContacts?.length || 0} contact{client.TeamClientContacts?.length !== 1 ? 's' : ''}
                                                            </span>
                                                        </>
                                                    )}
                                                    <div className="d-flex align-items-center gap-2 ms-auto" onClick={e => e.stopPropagation()}>
                                                        {canManage && editingClientId !== client.client_id && (
                                                            <i className="bi bi-pencil teams-action-icon" title="Rename" onClick={() => { setEditingClientId(client.client_id); setEditingClientName(client.client_name); }} />
                                                        )}
                                                        {canManage && (
                                                            <i className="bi bi-archive teams-action-icon teams-archive-icon" title="Archive" onClick={() => handleArchiveClient(client.client_id)} />
                                                        )}
                                                        <i className={`bi ${expandedClientId === client.client_id ? 'bi-chevron-up' : 'bi-chevron-down'} teams-action-icon`} onClick={() => toggleExpandClient(client.client_id)} />
                                                    </div>
                                                </div>

                                                {/* Expanded contacts panel */}
                                                {expandedClientId === client.client_id && (
                                                    <div className="teams-client-contacts">
                                                        {client.TeamClientContacts?.length > 0 ? (
                                                            client.TeamClientContacts.map(contact => (
                                                                <div key={contact.contact_id} className="teams-contact-row">
                                                                    {editingContact?.contactId === contact.contact_id ? (
                                                                        <div className="teams-contact-form">
                                                                            <div className="row g-2 mb-2">
                                                                                <div className="col-6">
                                                                                    <input className="form-control form-control-sm" placeholder="First name *" value={editingContact.form.first_name} onChange={e => setEditingContact(ec => ({ ...ec, form: { ...ec.form, first_name: e.target.value } }))} />
                                                                                </div>
                                                                                <div className="col-6">
                                                                                    <input className="form-control form-control-sm" placeholder="Last name *" value={editingContact.form.last_name} onChange={e => setEditingContact(ec => ({ ...ec, form: { ...ec.form, last_name: e.target.value } }))} />
                                                                                </div>
                                                                                <div className="col-12">
                                                                                    <input className="form-control form-control-sm" placeholder="Title" value={editingContact.form.title} onChange={e => setEditingContact(ec => ({ ...ec, form: { ...ec.form, title: e.target.value } }))} />
                                                                                </div>
                                                                                <div className="col-6">
                                                                                    <input className="form-control form-control-sm" placeholder="Phone" value={editingContact.form.phone} onChange={e => setEditingContact(ec => ({ ...ec, form: { ...ec.form, phone: formatPhoneInput(e.target.value) } }))} />
                                                                                </div>
                                                                                <div className="col-6">
                                                                                    <input className="form-control form-control-sm" placeholder="Email" value={editingContact.form.email} onChange={e => setEditingContact(ec => ({ ...ec, form: { ...ec.form, email: e.target.value } }))} />
                                                                                </div>
                                                                                <div className="col-12">
                                                                                    <input className="form-control form-control-sm" placeholder="Address" value={editingContact.form.address} onChange={e => setEditingContact(ec => ({ ...ec, form: { ...ec.form, address: e.target.value } }))} />
                                                                                </div>
                                                                                <div className="col-12">
                                                                                    <textarea className="form-control form-control-sm" placeholder="Notes (max 5000 chars)" maxLength={5000} rows={3} value={editingContact.form.notes} onChange={e => setEditingContact(ec => ({ ...ec, form: { ...ec.form, notes: e.target.value } }))} />
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex gap-2">
                                                                                <button className="btn btn-sm btn-primary" onClick={() => handleSaveContact(client.client_id, contact.contact_id)}>Save</button>
                                                                                <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingContact(null)}>Cancel</button>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="teams-contact-detail">
                                                                            <div className="teams-contact-name">{contact.first_name} {contact.last_name}</div>
                                                                            {contact.title && <div className="teams-contact-title">{contact.title}</div>}
                                                                            {contact.phone && <div className="teams-contact-meta"><i className="bi bi-telephone me-1" />{contact.phone}</div>}
                                                                            {contact.email && <div className="teams-contact-meta"><i className="bi bi-envelope me-1" /><a href={`mailto:${contact.email}`}>{contact.email}</a></div>}
                                                                            {contact.address && <div className="teams-contact-meta"><i className="bi bi-geo-alt me-1" />{contact.address}</div>}
                                                                            {contact.notes && <div className="teams-contact-notes">{contact.notes}</div>}
                                                                            {canManage && (
                                                                                <div className="d-flex gap-2 mt-2">
                                                                                    <button className="btn btn-xs teams-contact-edit-btn" onClick={() => setEditingContact({ contactId: contact.contact_id, form: { first_name: contact.first_name, last_name: contact.last_name, title: contact.title || '', phone: contact.phone || '', email: contact.email || '', address: contact.address || '', notes: contact.notes || '' } })}>Edit</button>
                                                                                    <button className="btn btn-xs teams-contact-delete-btn" onClick={() => handleDeleteContact(client.client_id, contact.contact_id)}>Remove</button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <p className="text-muted small fst-italic mb-2">No contacts yet.</p>
                                                        )}

                                                        {/* Add contact form */}
                                                        {canManage && !showAddContact && !editingContact && (
                                                            <button className="btn btn-sm teams-add-contact-btn mt-1" onClick={() => { setShowAddContact(true); setContactForm(BLANK_CONTACT); }}>
                                                                <i className="bi bi-plus me-1" />Add Contact
                                                            </button>
                                                        )}

                                                        {/* ── Feedback Requests section ── */}
                                                        <div className="teams-feedback-section">
                                                            <div className="teams-feedback-header">
                                                                <span className="teams-feedback-title">Feedback Requests</span>
                                                            </div>

                                                            {(feedbackRequests[client.client_id] || []).length === 0 ? (
                                                                <p className="text-muted small fst-italic mb-0">No feedback requests yet.</p>
                                                            ) : (
                                                                <div className="teams-feedback-list">
                                                                    {(feedbackRequests[client.client_id] || []).map(req => {
                                                                        const isExpired = new Date(req.expires_at) < new Date();
                                                                        const billCount = req.ClientFeedbackRequestBills?.length || 0;
                                                                        const responseCount = req.ClientFeedbackResponses?.filter(r => r.submitted_at).length || 0;
                                                                        const recipientCount = req.ClientFeedbackRecipients?.length || 0;
                                                                        const isOpen = expandedFeedbackId === req.id;

                                                                        return (
                                                                            <div key={req.id} className="teams-feedback-row">
                                                                                <div
                                                                                    className="teams-feedback-row-header"
                                                                                    onClick={() => setExpandedFeedbackId(isOpen ? null : req.id)}
                                                                                >
                                                                                    <div className="teams-feedback-row-main">
                                                                                        <span className="teams-feedback-row-title">{req.title}</span>
                                                                                        <span className={`teams-feedback-status ${isExpired ? 'teams-feedback-status--expired' : 'teams-feedback-status--active'}`}>
                                                                                            {isExpired ? 'Expired' : 'Active'}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="teams-feedback-row-meta">
                                                                                        <span>{billCount} bill{billCount !== 1 ? 's' : ''}</span>
                                                                                        <span>·</span>
                                                                                        <span>{recipientCount} recipient{recipientCount !== 1 ? 's' : ''}</span>
                                                                                        <span>·</span>
                                                                                        <span>{responseCount} submitted</span>
                                                                                        <span>·</span>
                                                                                        <span>Created on {new Date(req.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                                                                        <span>·</span>
                                                                                        <span>Closes on {new Date(req.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                                                                        <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} ms-1`} />
                                                                                    </div>
                                                                                </div>

                                                                                {isOpen && (
                                                                                    <div className="teams-feedback-detail">
                                                                                        {(req.ClientFeedbackRecipients || []).map(recipient => {
                                                                                            const contact = recipient.TeamClientContact;
                                                                                            if (!contact) return null;
                                                                                            const contactName = `${contact.first_name} ${contact.last_name}`;
                                                                                            const contactResponses = (req.ClientFeedbackResponses || [])
                                                                                                .filter(r => r.contact_id === recipient.contact_id && r.submitted_at);

                                                                                            return (
                                                                                                <div key={recipient.contact_id} className="teams-feedback-respondent">
                                                                                                    <div className="teams-feedback-respondent-name">
                                                                                                        <i className="bi bi-person me-1" />
                                                                                                        {contactName}
                                                                                                        {contactResponses.length > 0
                                                                                                            ? <span className="teams-feedback-submitted-badge">Submitted</span>
                                                                                                            : <span className="teams-feedback-pending-badge">Pending</span>
                                                                                                        }
                                                                                                    </div>
                                                                                                    {contactResponses.length > 0 && (
                                                                                                        <div className="teams-feedback-bill-notes">
                                                                                                            {contactResponses.map(r => {
                                                                                                                const billInfo = (req.ClientFeedbackRequestBills || []).find(b => b.bill_id === r.bill_id)?.Bill;
                                                                                                                const billLabel = billInfo
                                                                                                                    ? `${billInfo.bill_type} ${billInfo.bill_number}${billInfo.Author ? ` (${billInfo.Author.first_name} ${billInfo.Author.last_name})` : ''} — ${billInfo.title}`
                                                                                                                    : `Bill #${r.bill_id}`;
                                                                                                                return (
                                                                                                                    <div key={r.id} className="teams-feedback-bill-note">
                                                                                                                        <Link to={`/legislation/bill/${r.bill_id}`} className="teams-feedback-bill-id">{billLabel}</Link>
                                                                                                                        <p className="teams-feedback-note-text">{r.notes || <em>No notes provided.</em>}</p>
                                                                                                                    </div>
                                                                                                                );
                                                                                                            })}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            );
                                                                                        })}

                                                                                        <div className="teams-feedback-detail-actions">
                                                                                            {canManage && (
                                                                                                <button
                                                                                                    className="btn btn-xs teams-feedback-delete-btn"
                                                                                                    onClick={() => {
                                                                                                        if (!window.confirm('Delete this feedback request? This cannot be undone.')) return;
                                                                                                        API.deleteFeedbackRequest(req.id)
                                                                                                            .then(() => loadFeedbackRequests(client.client_id))
                                                                                                            .catch(err => console.error('Error deleting request:', err));
                                                                                                    }}
                                                                                                >
                                                                                                    <i className="bi bi-trash3 me-1" />Delete
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {canManage && showAddContact && (
                                                            <div className="teams-contact-form mt-2">
                                                                <div className="row g-2 mb-2">
                                                                    <div className="col-6">
                                                                        <input className="form-control form-control-sm" placeholder="First name *" value={contactForm.first_name} onChange={e => setContactForm(f => ({ ...f, first_name: e.target.value }))} />
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <input className="form-control form-control-sm" placeholder="Last name *" value={contactForm.last_name} onChange={e => setContactForm(f => ({ ...f, last_name: e.target.value }))} />
                                                                    </div>
                                                                    <div className="col-12">
                                                                        <input className="form-control form-control-sm" placeholder="Title" value={contactForm.title} onChange={e => setContactForm(f => ({ ...f, title: e.target.value }))} />
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <input className="form-control form-control-sm" placeholder="Phone" value={contactForm.phone} onChange={e => setContactForm(f => ({ ...f, phone: formatPhoneInput(e.target.value) } ))} />
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <input className="form-control form-control-sm" placeholder="Email" value={contactForm.email} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} />
                                                                    </div>
                                                                    <div className="col-12">
                                                                        <input className="form-control form-control-sm" placeholder="Address" value={contactForm.address} onChange={e => setContactForm(f => ({ ...f, address: e.target.value }))} />
                                                                    </div>
                                                                    <div className="col-12">
                                                                        <textarea className="form-control form-control-sm" placeholder="Notes (max 5000 chars)" maxLength={5000} rows={3} value={contactForm.notes} onChange={e => setContactForm(f => ({ ...f, notes: e.target.value }))} />
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex gap-2">
                                                                    <button className="btn btn-sm btn-primary" onClick={() => handleAddContact(client.client_id)} disabled={!contactForm.first_name.trim() || !contactForm.last_name.trim()}>Add</button>
                                                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => { setShowAddContact(false); setContactForm(BLANK_CONTACT); }}>Cancel</button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Add client form */}
                                    {canManage && (
                                        <form className="d-flex gap-2 mt-3" onSubmit={handleAddClient}>
                                            <input
                                                className="form-control form-control-sm"
                                                placeholder={`New ${sectionLabelSingular.toLowerCase()} name…`}
                                                value={newClientName}
                                                onChange={e => setNewClientName(e.target.value)}
                                            />
                                            <button type="submit" className="btn btn-sm btn-primary text-nowrap" disabled={!newClientName.trim()}>Add {sectionLabelSingular}</button>
                                        </form>
                                    )}

                                    {/* Archived subsection */}
                                    {clients.some(c => c.archived) && (
                                        <div className="teams-archived-section">
                                            <button
                                                className="teams-archived-toggle"
                                                onClick={() => setShowArchivedClients(v => !v)}
                                            >
                                                <i className={`bi bi-chevron-${showArchivedClients ? 'up' : 'down'} me-1`} />
                                                Archived ({clients.filter(c => c.archived).length})
                                            </button>
                                            {showArchivedClients && (
                                                <div className="teams-archived-list">
                                                    {clients.filter(c => c.archived).map(client => (
                                                        <div key={client.client_id} className="teams-archived-row">
                                                            <span className="teams-archived-name">{client.client_name}</span>
                                                            {canManage && (
                                                                <div className="teams-archived-actions">
                                                                    <button
                                                                        className="btn btn-sm teams-unarchive-btn"
                                                                        onClick={() => handleUnarchiveClient(client.client_id)}
                                                                    >
                                                                        Restore
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm teams-delete-client-btn"
                                                                        onClick={() => handleDeleteClient(client.client_id, client.client_name)}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                            <div className="mt-5 alert alert-light">
                                <small className="text-muted">
                                    <strong>Note:</strong> Team Lists and Tracked Bills will appear in the Legislation section once you are a member of a team.
                                </small>
                            </div>
                        </>)}

                        {/* ── Bill Work tab ── */}
                        {teamTab === 'billwork' && (
                            <div className="tw-bill-work">
                                <div className="tw-bill-work-toolbar mb-3">
                                    <div className="tw-bill-filter-bar tw-bill-filter-bar--session">
                                        <label className="tw-section-label me-2">Session:</label>
                                        <select className="form-select form-select-sm tw-bulk-select" value={billSessionFilter} onChange={e => setBillSessionFilter(e.target.value)}>
                                            {billWorkSessionOptions.map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="tw-bill-filter-bar">
                                        <label className="tw-section-label me-2">Team Member:</label>
                                        <select className="form-select form-select-sm tw-bulk-select" value={billAssigneeFilter} onChange={e => setBillAssigneeFilter(e.target.value)}>
                                            <option value="">All bills</option>
                                            {selectedTeam.TeamMembers.map(m => (
                                                <option key={m.user_id} value={m.user_id}>{m.ProfileInfo.first_name} {m.ProfileInfo.last_name}</option>
                                            ))}
                                        </select>
                                        <button
                                            className={`tw-filter-clear-btn${billAssigneeFilter ? '' : ' invisible'}`}
                                            onClick={() => setBillAssigneeFilter('')}
                                            tabIndex={billAssigneeFilter ? 0 : -1}
                                            title="Clear filter"
                                        >
                                            <i className="bi bi-x-lg" />
                                        </button>
                                    </div>
                                    <div className="tw-bill-filter-bar">
                                        <label className="tw-section-label me-2">Subject:</label>
                                        <select className="form-select form-select-sm tw-bulk-select" value={billSubjectTagFilter} onChange={e => setBillSubjectTagFilter(e.target.value)}>
                                            <option value="">All bills</option>
                                            {teamSubjectTags.map(t => (
                                                <option key={t.tag_id} value={t.tag_id}>{t.name}</option>
                                            ))}
                                        </select>
                                        <button
                                            className={`tw-filter-clear-btn${billSubjectTagFilter ? '' : ' invisible'}`}
                                            onClick={() => setBillSubjectTagFilter('')}
                                            tabIndex={billSubjectTagFilter ? 0 : -1}
                                            title="Clear filter"
                                        >
                                            <i className="bi bi-x-lg" />
                                        </button>
                                    </div>
                                    <div className="tw-bill-filter-bar">
                                        <label className="tw-section-label me-2">{isClientLabelType ? 'Client' : 'Stakeholder'}:</label>
                                        <select className="form-select form-select-sm tw-bulk-select" value={billClientFilter} onChange={e => setBillClientFilter(e.target.value)}>
                                            <option value="">All bills</option>
                                            <option value="__unassigned__">No {sectionLabel.toLowerCase()} assigned</option>
                                            {billWorkClientOptions.map(c => (
                                                <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                                            ))}
                                        </select>
                                        <button
                                            className={`tw-filter-clear-btn${billClientFilter ? '' : ' invisible'}`}
                                            onClick={() => setBillClientFilter('')}
                                            tabIndex={billClientFilter ? 0 : -1}
                                            title="Clear filter"
                                        >
                                            <i className="bi bi-x-lg" />
                                        </button>
                                    </div>
                                    <div className="tw-bill-work-actions">
                                        {canManage && (
                                            <button className="tw-reassign-trigger" onClick={() => setShowManageTags(v => !v)}>
                                                <i className="bi bi-tags me-1" />Manage Tags
                                            </button>
                                        )}
                                        {canManage && selectedTeam.TeamMembers.length > 1 && (
                                            <button className="tw-reassign-trigger" onClick={() => { setShowBulkReassign(true); setBulkResult(null); }}>
                                                <i className="bi bi-arrow-left-right me-1" />Bulk Reassign
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {showManageTags && canManage && (
                                    <div className="tw-manage-tags-panel mb-3">
                                        <div className="tw-manage-tags-list">
                                            {teamSubjectTags.length === 0 && <span className="text-muted small fst-italic">No subject tags yet.</span>}
                                            {teamSubjectTags.map(t => (
                                                <span key={t.tag_id} className="tw-tag-chip tw-tag-chip--managed">
                                                    {t.name}
                                                    <button className="tw-tag-remove-btn" title="Delete tag" onClick={() => handleDeleteSubjectTagDef(t.tag_id)}><i className="bi bi-x" /></button>
                                                </span>
                                            ))}
                                        </div>
                                        <form className="d-flex gap-2 mt-2" onSubmit={handleCreateSubjectTag}>
                                            <input
                                                className="form-control form-control-sm"
                                                placeholder="New subject tag…"
                                                value={newTagName}
                                                onChange={e => setNewTagName(e.target.value)}
                                                maxLength={60}
                                            />
                                            <button type="submit" className="btn btn-sm btn-primary text-nowrap" disabled={!newTagName.trim()}>Add Tag</button>
                                        </form>
                                    </div>
                                )}

                                {billWorkLoading ? (
                                    <div className="text-center py-4"><span className="spinner-border spinner-border-sm text-secondary" /></div>
                                ) : trackedBillsOverview.length === 0 ? (
                                    <p className="text-muted fst-italic small">No bills are being tracked by this workspace yet. Track bills from the Legislation page to assign members and add tasks.</p>
                                ) : (
                                    <div className="tw-bill-list">
                                        {trackedBillsOverview.filter(bill =>
                                            (!billSessionFilter || formatSessionDashed(bill.session) === billSessionFilter) &&
                                            (!billAssigneeFilter || bill.assignments.some(a => a.user_id === billAssigneeFilter)) &&
                                            (!billSubjectTagFilter || bill.subjectTags.some(t => String(t.tag_id) === billSubjectTagFilter)) &&
                                            (!billClientFilter || (billClientFilter === '__unassigned__'
                                                ? (bill.clients || []).length === 0
                                                : (bill.clients || []).some(c => String(c.client_id) === billClientFilter)))
                                        ).map(bill => {
                                            const isExpanded = expandedBillId === bill.bill_id;
                                            const leads = bill.assignments.filter(a => a.role === 'Lead');
                                            const monitors = bill.assignments.filter(a => a.role === 'Monitor');
                                            const tasks_ = billTasks[bill.bill_id] || [];
                                            const notes_ = billNotes[bill.bill_id] || [];
                                            const currentAssignForm = addAssignForm[bill.bill_id] || { userId: '', role: 'Monitor' };
                                            const alreadyAssigned = bill.assignments.map(a => a.user_id);
                                            const availableMembers = selectedTeam.TeamMembers.filter(m => !alreadyAssigned.includes(m.user_id));
                                            const leadCount = leads.length;
                                            const isLeadOnBill = bill.assignments.some(a => a.user_id === auth.user.user_id && a.role === 'Lead');
                                            const canAssignOthers = canManage || isLeadOnBill;
                                            const canEditTasks = canManage || isLeadOnBill;
                                            const availableTagsForBill = teamSubjectTags.filter(t => !bill.subjectTags.some(bt => bt.tag_id === t.tag_id));
                                            return (
                                                <div key={bill.bill_id} className="tw-bill-row">
                                                    <div className="tw-bill-header" onClick={() => handleBillExpand(bill.bill_id)}>
                                                        <Link className="tw-bill-id" to={`/legislation/bill/${bill.bill_id}`} onClick={e => e.stopPropagation()}>{bill.bill_type} {bill.bill_number}</Link>
                                                        <span className="tw-bill-title">{bill.title}</span>
                                                        <div className="tw-bill-chips">
                                                            {leads.map(a => (
                                                                <span key={a.id} className="tw-role-chip tw-role-chip--lead" title={`${a.first_name} ${a.last_name}`}>{a.first_name?.[0]}{a.last_name?.[0]} Lead</span>
                                                            ))}
                                                            {monitors.map(a => (
                                                                <span key={a.id} className="tw-role-chip tw-role-chip--monitor" title={`${a.first_name} ${a.last_name}`}>{a.first_name?.[0]}{a.last_name?.[0]}</span>
                                                            ))}
                                                            {bill.open_task_count > 0 && (
                                                                <span className="tw-open-tasks-badge">{bill.open_task_count} open</span>
                                                            )}
                                                            {(bill.clients || []).map(c => (
                                                                <span key={c.client_id} className="tw-client-chip" title={sectionLabelSingular}>{c.client_name}</span>
                                                            ))}
                                                            {bill.subjectTags.map(t => (
                                                                <span key={t.tag_id} className="tw-tag-chip">{t.name}</span>
                                                            ))}
                                                        </div>
                                                        <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} tw-expand-icon`} />
                                                    </div>

                                                    {isExpanded && (
                                                        <div className="tw-bill-sections">
                                                            {/* Assignments */}
                                                            <div className="tw-bill-section">
                                                                <div className="tw-bill-section-title"><i className="bi bi-person-badge me-1" />Assignments</div>
                                                                {bill.assignments.length === 0 && <p className="text-muted small fst-italic mb-2">No one assigned yet.</p>}
                                                                <div className="tw-assignment-list">
                                                                    {bill.assignments.map(a => (
                                                                        <div key={a.id} className="tw-assignment-item">
                                                                            <span className="tw-assignee-name">{a.first_name} {a.last_name}</span>
                                                                            {canManage ? (
                                                                                <select className={`tw-role-select tw-role-select--${a.role.toLowerCase()}`} value={a.role} onChange={e => handleRoleChange(bill.bill_id, a.id, e.target.value)}>
                                                                                    <option value="Lead" disabled={a.role !== 'Lead' && leadCount >= 2}>Lead{a.role !== 'Lead' && leadCount >= 2 ? ' (max 2)' : ''}</option>
                                                                                    <option value="Monitor">Monitor</option>
                                                                                </select>
                                                                            ) : (
                                                                                <span className={`tw-role-chip tw-role-chip--${a.role.toLowerCase()}`}>{a.role}</span>
                                                                            )}
                                                                            {canManage && <button className="tw-remove-btn" title="Remove" onClick={() => handleRemoveAssignment(bill.bill_id, a.id)}><i className="bi bi-x" /></button>}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {canManage && availableMembers.length > 0 && (
                                                                    <div className="tw-add-assign-form mt-2">
                                                                        <select className="form-select form-select-sm tw-add-select" value={currentAssignForm.userId} onChange={e => setAddAssignForm(prev => ({ ...prev, [bill.bill_id]: { ...currentAssignForm, userId: e.target.value } }))}>
                                                                            <option value="">Assign member…</option>
                                                                            {availableMembers.map(m => (
                                                                                <option key={m.user_id} value={m.user_id}>{m.ProfileInfo.first_name} {m.ProfileInfo.last_name}</option>
                                                                            ))}
                                                                        </select>
                                                                        <select className="form-select form-select-sm tw-add-select" value={currentAssignForm.role} onChange={e => setAddAssignForm(prev => ({ ...prev, [bill.bill_id]: { ...currentAssignForm, role: e.target.value } }))}>
                                                                            <option value="Monitor">Monitor</option>
                                                                            <option value="Lead" disabled={leadCount >= 2}>Lead{leadCount >= 2 ? ' (max 2)' : ''}</option>
                                                                        </select>
                                                                        <button className="btn btn-sm btn-primary" disabled={!currentAssignForm.userId} onClick={() => handleAddAssignment(bill.bill_id)}>Assign</button>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Tasks */}
                                                            <div className="tw-bill-section">
                                                                <div className="tw-bill-section-title"><i className="bi bi-check2-square me-1" />Tasks</div>
                                                                {tasks_.length === 0 && <p className="text-muted small fst-italic mb-2">No tasks yet.</p>}
                                                                {tasks_.length > 0 && (
                                                                    <div className="tw-task-list-header">
                                                                        <span />
                                                                        <span>Task</span>
                                                                        <span>Assignee</span>
                                                                        <span>Due</span>
                                                                        <span>Priority</span>
                                                                        {canEditTasks && <span />}
                                                                    </div>
                                                                )}
                                                                <div className="tw-task-list">
                                                                    {tasks_.map(task => {
                                                                        const overdue = task.due_date && !task.completed && task.due_date < TODAY_PT;
                                                                        return (
                                                                        <div key={task.id} className={`tw-task-item${task.completed ? ' tw-task-complete' : ''}${overdue ? ' tw-task-item--overdue' : ''}`}>
                                                                            <input type="checkbox" className="tw-task-check" checked={task.completed} onChange={() => handleToggleTask(bill.bill_id, task)} />
                                                                            <span className="tw-task-text">{task.task_text}</span>
                                                                            {canEditTasks ? (
                                                                                <select className="tw-inline-select" value={task.assigned_to_user_id || ''} onChange={e => handleUpdateTask(bill.bill_id, task.id, { assigned_to_user_id: e.target.value || null })}>
                                                                                    <option value="">—</option>
                                                                                    {selectedTeam.TeamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.ProfileInfo.first_name} {m.ProfileInfo.last_name[0]}.</option>)}
                                                                                </select>
                                                                            ) : (
                                                                                <span className="tw-task-col-val">{task.Assignee ? `${task.Assignee.first_name} ${task.Assignee.last_name[0]}.` : '—'}</span>
                                                                            )}
                                                                            {canEditTasks ? (
                                                                                <input type="date" className="tw-inline-date" value={task.due_date || ''} onChange={e => handleUpdateTask(bill.bill_id, task.id, { due_date: e.target.value || null })} />
                                                                            ) : (
                                                                                <span className={`tw-task-col-val${overdue ? ' tw-task-overdue' : ''}`}>
                                                                                    {overdue && <i className="bi bi-exclamation-circle-fill me-1" />}
                                                                                    {task.due_date ? new Date(task.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                                                                                </span>
                                                                            )}
                                                                            {canEditTasks ? (
                                                                                <select className={`tw-priority-select tw-priority--${(task.priority || 'none').toLowerCase()}`} value={task.priority || ''} onChange={e => handleUpdateTask(bill.bill_id, task.id, { priority: e.target.value || null })}>
                                                                                    <option value="">—</option>
                                                                                    <option value="Low">Low</option>
                                                                                    <option value="Medium">Medium</option>
                                                                                    <option value="High">High</option>
                                                                                    <option value="Urgent">Urgent</option>
                                                                                </select>
                                                                            ) : (
                                                                                <span className={`tw-task-col-val tw-task-priority-label tw-priority-label--${(task.priority || 'none').toLowerCase()}`}>{task.priority || '—'}</span>
                                                                            )}
                                                                            {canEditTasks && <button className="tw-remove-btn" onClick={() => handleDeleteTask(bill.bill_id, task.id)}><i className="bi bi-trash3" /></button>}
                                                                        </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                                <div className="tw-add-task-form mt-2">
                                                                    <input className="form-control form-control-sm tw-task-input" placeholder="New task…" value={newTaskText[bill.bill_id] || ''} onChange={e => setNewTaskText(prev => ({ ...prev, [bill.bill_id]: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleAddTask(bill.bill_id)} />
                                                                    <select className="form-select form-select-sm tw-add-select" value={newTaskAssignee[bill.bill_id] || ''} onChange={e => setNewTaskAssignee(prev => ({ ...prev, [bill.bill_id]: e.target.value }))}>
                                                                        <option value="">Unassigned</option>
                                                                        {(canAssignOthers ? selectedTeam.TeamMembers : selectedTeam.TeamMembers.filter(m => m.user_id === auth.user.user_id)).map(m => <option key={m.user_id} value={m.user_id}>{m.ProfileInfo.first_name} {m.ProfileInfo.last_name}</option>)}
                                                                    </select>
                                                                    <span className="tw-date-label">Due:</span>
                                                                    <input type="date" className="form-control form-control-sm tw-date-input" value={newTaskDue[bill.bill_id] || ''} onChange={e => setNewTaskDue(prev => ({ ...prev, [bill.bill_id]: e.target.value }))} />
                                                                    <select className="form-select form-select-sm tw-add-select" value={newTaskPriority[bill.bill_id] || ''} onChange={e => setNewTaskPriority(prev => ({ ...prev, [bill.bill_id]: e.target.value }))}>
                                                                        <option value="">Priority</option>
                                                                        <option value="Low">Low</option>
                                                                        <option value="Medium">Medium</option>
                                                                        <option value="High">High</option>
                                                                        <option value="Urgent">Urgent</option>
                                                                    </select>
                                                                    <button className="btn btn-sm btn-primary" disabled={!(newTaskText[bill.bill_id]?.trim())} onClick={() => handleAddTask(bill.bill_id)}>Add</button>
                                                                </div>
                                                            </div>

                                                            {/* Notes */}
                                                            <div className="tw-bill-section">
                                                                <div className="tw-bill-section-title"><i className="bi bi-journal-text me-1" />Notes</div>
                                                                {notes_.length === 0 && <p className="text-muted small fst-italic mb-2">No notes yet.</p>}
                                                                <div className="tw-note-list">
                                                                    {notes_.map(note => (
                                                                        <div key={note.id} className="tw-note-item">
                                                                            <p className="tw-note-text mb-1">{note.note_text}</p>
                                                                            <div className="tw-note-footer">
                                                                                <div className="tw-note-meta">
                                                                                    {note.Author && <span>{note.Author.first_name} {note.Author.last_name}</span>}
                                                                                    <span>{new Date(note.createdAt || note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                                                                </div>
                                                                                {(note.created_by_user_id === auth.user.user_id || canManage) && (
                                                                                    <button className="tw-note-delete-btn" title="Delete note" onClick={() => handleDeleteNote(bill.bill_id, note.id)}><i className="bi bi-trash3" /></button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                <div className="tw-add-note-form mt-2">
                                                                    <textarea className="form-control form-control-sm" rows={2} placeholder="Log a note…" value={newNoteText[bill.bill_id] || ''} onChange={e => setNewNoteText(prev => ({ ...prev, [bill.bill_id]: e.target.value }))} />
                                                                    <button className="btn btn-sm btn-primary mt-1" disabled={!(newNoteText[bill.bill_id]?.trim())} onClick={() => handleAddNote(bill.bill_id)}>Add Note</button>
                                                                </div>
                                                            </div>

                                                            {/* Subject Tags */}
                                                            <div className="tw-bill-section tw-bill-section--last">
                                                                <div className="tw-bill-section-title"><i className="bi bi-tags me-1" />Subject Tags</div>
                                                                {bill.subjectTags.length === 0 && <p className="text-muted small fst-italic mb-2">No subject tags on this bill.</p>}
                                                                <div className="tw-manage-tags-list mb-2">
                                                                    {bill.subjectTags.map(t => (
                                                                        <span key={t.tag_id} className="tw-tag-chip">
                                                                            {t.name}
                                                                            {canBill && (
                                                                                <button className="tw-tag-remove-btn" title="Remove tag" onClick={() => handleRemoveSubjectTagFromBill(bill.bill_id, t.tag_id)}><i className="bi bi-x" /></button>
                                                                            )}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                                {canBill && availableTagsForBill.length > 0 && (
                                                                    <div className="tw-add-assign-form mt-2">
                                                                        <select className="form-select form-select-sm tw-add-select" value={addTagSelect[bill.bill_id] || ''} onChange={e => setAddTagSelect(prev => ({ ...prev, [bill.bill_id]: e.target.value }))}>
                                                                            <option value="">Add subject tag…</option>
                                                                            {availableTagsForBill.map(t => (
                                                                                <option key={t.tag_id} value={t.tag_id}>{t.name}</option>
                                                                            ))}
                                                                        </select>
                                                                        <button className="btn btn-sm btn-primary" disabled={!addTagSelect[bill.bill_id]} onClick={() => handleAddSubjectTagToBill(bill.bill_id)}>Add</button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Tasks dashboard tab ── */}
                        {teamTab === 'tasks' && (
                            <div className="tw-task-dashboard">
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <h5 className="mb-0">Tasks</h5>
                                    <div className="tw-filter-toggle">
                                        <button className={`tw-filter-btn${taskDashFilter === 'all' ? ' active' : ''}`} onClick={() => { setTaskDashFilter('all'); fetchTeamTasks('all'); }}>All</button>
                                        <button className={`tw-filter-btn${taskDashFilter === 'mine' ? ' active' : ''}`} onClick={() => { setTaskDashFilter('mine'); fetchTeamTasks('mine'); }}>Mine</button>
                                    </div>
                                </div>
                                {teamTasksLoading ? (
                                    <div className="text-center py-4"><span className="spinner-border spinner-border-sm text-secondary" /></div>
                                ) : teamTasks.length === 0 ? (
                                    <p className="text-muted fst-italic small">{taskDashFilter === 'mine' ? 'No tasks assigned to you.' : 'No tasks found.'}</p>
                                ) : (
                                    <>
                                    <div className="tw-task-list-header tw-task-list-header--dashboard">
                                        <span />
                                        <span>Task</span>
                                        <span>Topic</span>
                                        <span>Assignee</span>
                                        <span>Due</span>
                                        <span>Priority</span>
                                        <span />
                                    </div>
                                    <div className="tw-task-list tw-task-list--dashboard">
                                        {(() => {
                                            const leadBillIds = new Set(
                                                trackedBillsOverview
                                                    .filter(b => b.assignments.some(a => a.user_id === auth.user.user_id && a.role === 'Lead'))
                                                    .map(b => b.bill_id)
                                            );
                                            return teamTasks.map(task => {
                                            const overdue = task.due_date && !task.completed && task.due_date < TODAY_PT;
                                            const canEdit = canManage || leadBillIds.has(task.bill_id)
                                                || (!task.bill_id && task.created_by_user_id === auth.user.user_id);
                                            return (
                                            <div key={task.id} className={`tw-task-item tw-task-item--dashboard${task.completed ? ' tw-task-complete' : ''}${overdue ? ' tw-task-item--overdue' : ''}`}>
                                                <input type="checkbox" className="tw-task-check" checked={task.completed} onChange={async () => {
                                                    const nowComplete = !task.completed;
                                                    setTeamTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: nowComplete } : t));
                                                    try {
                                                        if (!task.bill_id) {
                                                            const res = await API.updateTeamTask(selectedTeam.team_id, task.id, { completed: nowComplete, requesting_user_id: auth.user.user_id });
                                                            if (res.data.rulemaking_ref) setTeamRulemakingTasks(prev => prev.map(t => t.id === task.id ? res.data : t));
                                                        } else {
                                                            await API.updateBillTask(selectedTeam.team_id, task.bill_id, task.id, { completed: nowComplete, requesting_user_id: auth.user.user_id });
                                                        }
                                                    } catch (err) { setTeamTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: task.completed } : t)); }
                                                }} />
                                                <span className="tw-task-text">
                                                    {task.task_text}
                                                    {task.step_count > 0 && (
                                                        <span className="tw-task-steps" title={`${task.steps_done} of ${task.step_count} steps done`}>
                                                            <i className="bi bi-list-check" /> {task.steps_done}/{task.step_count}
                                                        </span>
                                                    )}
                                                </span>
                                                {task.rulemaking_ref ? (
                                                    <span className="tw-task-bill-label" title={task.rulemaking_title || undefined}>
                                                        {task.rulemaking_url
                                                            ? <a href={task.rulemaking_url} target="_blank" rel="noopener noreferrer">Rulemaking</a>
                                                            : 'Rulemaking'}
                                                    </span>
                                                ) : task.bill_id ? (
                                                    <span className="tw-task-bill-label">{task.Bill?.bill_type} {task.Bill?.bill_number}</span>
                                                ) : (
                                                    <span className="tw-task-bill-label">General</span>
                                                )}
                                                {canEdit ? (
                                                    <select className="tw-inline-select" value={task.assigned_to_user_id || ''} onChange={e => handleUpdateTask(task.bill_id, task.id, { assigned_to_user_id: e.target.value || null })}>
                                                        <option value="">—</option>
                                                        {selectedTeam.TeamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.ProfileInfo.first_name} {m.ProfileInfo.last_name[0]}.</option>)}
                                                    </select>
                                                ) : (
                                                    <span className="tw-task-col-val">{task.Assignee ? `${task.Assignee.first_name} ${task.Assignee.last_name[0]}.` : '—'}</span>
                                                )}
                                                {canEdit ? (
                                                    <input type="date" className="tw-inline-date" value={task.due_date || ''} onChange={e => handleUpdateTask(task.bill_id, task.id, { due_date: e.target.value || null })} />
                                                ) : (
                                                    <span className={`tw-task-col-val${overdue ? ' tw-task-overdue' : ''}`}>
                                                        {overdue && <i className="bi bi-exclamation-circle-fill me-1" />}
                                                        {task.due_date ? new Date(task.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                                                    </span>
                                                )}
                                                {canEdit ? (
                                                    <select className={`tw-priority-select tw-priority--${(task.priority || 'none').toLowerCase()}`} value={task.priority || ''} onChange={e => handleUpdateTask(task.bill_id, task.id, { priority: e.target.value || null })}>
                                                        <option value="">—</option>
                                                        <option value="Low">Low</option>
                                                        <option value="Medium">Medium</option>
                                                        <option value="High">High</option>
                                                        <option value="Urgent">Urgent</option>
                                                    </select>
                                                ) : (
                                                    <span className={`tw-task-col-val tw-task-priority-label tw-priority-label--${(task.priority || 'none').toLowerCase()}`}>{task.priority || '—'}</span>
                                                )}
                                                <button type="button" className="tw-task-detail-btn" onClick={() => setDetailTaskId(task.id)} title="Open task details" aria-label="Open task details">
                                                    <i className="bi bi-box-arrow-up-right" />
                                                </button>
                                            </div>
                                            );
                                            });
                                        })()}
                                    </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* ── Reports tab ── */}
                        {teamTab === 'reports' && (
                            <div className="tw-reports-tab">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h5 className="mb-0">Reports</h5>
                                </div>
                                {reportsLoading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                                    </div>
                                ) : reports.length === 0 ? (
                                    <p className="text-muted small fst-italic">No reports yet. Build one from the Legislation page.</p>
                                ) : (
                                    <div className="tw-report-list">
                                        {reports.map(report => {
                                            const billCount = report.ReportBills?.length || 0;
                                            const totalRecipients = report.ReportRecipients?.length || 0;
                                            const activeRecipients = (report.ReportRecipients || []).filter(r => new Date(r.expires_at) > new Date()).length;
                                            return (
                                                <div key={report.id} className="tw-report-row" onClick={() => setSelectedReportId(report.id)}>
                                                    <div className="tw-report-row-main">
                                                        <span className="tw-report-row-title">{report.title}</span>
                                                    </div>
                                                    <div className="tw-report-row-meta">
                                                        <span>{report.TeamClient?.client_name || 'Internal only'}</span>
                                                        <span>·</span>
                                                        <span>{billCount} bill{billCount !== 1 ? 's' : ''}</span>
                                                        <span>·</span>
                                                        <span>{activeRecipients} of {totalRecipients} active recipient{totalRecipients !== 1 ? 's' : ''}</span>
                                                        <span>·</span>
                                                        <span>By {report.UserAccount?.first_name} {report.UserAccount?.last_name}</span>
                                                        <span>·</span>
                                                        <span>Created on {new Date(report.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {teamTab === 'rulemaking' && (
                            <div className="tw-reports-tab">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h5 className="mb-0">Rulemaking Ahead</h5>
                                </div>
                                <p className="text-muted small mb-3">
                                    Bills your Workspace tracks, for itself or for a client / stakeholder, that were recently signed into law and still face rulemaking, from the bill digest and proposed regulations in the OAL Notice Register. Matching is heuristic.
                                    {teamRulemaking && teamRulemaking.notices_available === false && ' Notice Register data has not been loaded yet, so hearing dates are not shown.'}
                                </p>
                                {teamRulemakingLoading || !teamRulemaking ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                                    </div>
                                ) : teamRulemaking.failed ? (
                                    <p className="text-muted small fst-italic">Unable to load rulemaking information.</p>
                                ) : teamRulemaking.bills.length === 0 ? (
                                    <p className="text-muted small fst-italic">No enacted bills tracked by this Workspace are currently facing rulemaking.</p>
                                ) : (
                                    <WorkspaceRulemakingList
                                        bills={teamRulemaking.bills}
                                        sectionLabel={sectionLabel}
                                        sectionLabelSingular={sectionLabelSingular}
                                        tasking={{
                                            teams: [{
                                                team_id: selectedTeam.team_id,
                                                team_name: selectedTeam.team_name,
                                                can_assign: canManage,
                                                members: selectedTeam.TeamMembers.map(m => ({
                                                    user_id: m.user_id,
                                                    name: `${(m.ProfileInfo || m.UserAccount)?.first_name || ''} ${(m.ProfileInfo || m.UserAccount)?.last_name || ''}`.trim(),
                                                })),
                                            }],
                                            tasks: teamRulemakingTasks,
                                            userId: auth.user.user_id,
                                            onCreated: task => setTeamRulemakingTasks(prev => [...prev, task]),
                                            onUpdated: task => setTeamRulemakingTasks(prev => prev.map(t => t.id === task.id ? task : t)),
                                            onDeleted: task => setTeamRulemakingTasks(prev => prev.filter(t => t.id !== task.id)),
                                        }}
                                    />
                                )}
                            </div>
                        )}

                        {teamTab === 'savedfilters' && (
                            <div className="tw-reports-tab">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h5 className="mb-0">Saved Filters</h5>
                                </div>
                                <p className="text-muted small mb-3">
                                    Filter presets saved here are shared with the whole Workspace and remain in place even if the member who created them leaves.
                                    {isOwner ? ' As the Workspace owner, you can rename or delete them here.' : ' Only the Workspace owner can rename or delete them.'}
                                </p>
                                {teamSavedFiltersLoading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                                    </div>
                                ) : teamSavedFilters.length === 0 ? (
                                    <p className="text-muted small fst-italic">No Workspace filters yet. Save one from the Legislation page's "Workspace Filters" panel.</p>
                                ) : (
                                    <ul className="list-unstyled mb-0">
                                        {teamSavedFilters.map(preset => {
                                            const criteria = describeSavedFilterCriteria(preset.filter_json, {
                                                authorNames: filterAuthorNames,
                                                locationNames: filterLocationNames,
                                                currentTeam: selectedTeam,
                                                clients,
                                            });
                                            return (
                                                <li key={preset.filter_id} className="teams-saved-filter-item">
                                                    {tsfEditingId === preset.filter_id ? (
                                                        <div className="d-flex gap-2 align-items-center">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm flex-grow-1"
                                                                value={tsfEditingName}
                                                                onChange={e => setTsfEditingName(e.target.value)}
                                                                onKeyDown={e => e.key === 'Enter' && handleTsfRename(preset.filter_id)}
                                                                autoFocus
                                                            />
                                                            <button className="btn btn-sm btn-primary" onClick={() => handleTsfRename(preset.filter_id)}>OK</button>
                                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setTsfEditingId(null)}>✕</button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <Link
                                                                    to="/legislation"
                                                                    state={{ loadFilter: preset }}
                                                                    className="teams-saved-filter-name"
                                                                    title="Go to Legislation to apply this filter set"
                                                                >
                                                                    <i className="bi bi-funnel me-1"></i>{preset.name}
                                                                </Link>
                                                                {isOwner && (
                                                                    <div className="d-flex gap-2">
                                                                        <button
                                                                            className="btn btn-link btn-sm p-0 text-muted"
                                                                            onClick={() => { setTsfEditingId(preset.filter_id); setTsfEditingName(preset.name); }}
                                                                            title="Rename"
                                                                        ><i className="bi bi-pencil"></i></button>
                                                                        <button
                                                                            className="btn btn-link btn-sm p-0 text-muted"
                                                                            onClick={() => handleTsfDelete(preset.filter_id)}
                                                                            title="Delete"
                                                                        ><i className="bi bi-trash"></i></button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {criteria.length > 0 ? (
                                                                <div className="tw-filter-params">
                                                                    {criteria.map((c, i) => (
                                                                        <span key={i} className="tw-filter-param"><strong>{c.label}:</strong> {c.value}</span>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div className="tw-filter-param-empty">No specific criteria — matches all bills.</div>
                                                            )}
                                                        </>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}

                        {teamTab === 'ambassador' && (
                            <div className="tw-reports-tab">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h5 className="mb-0">Ambassador Results</h5>
                                </div>
                                {ambassadorLoading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                                    </div>
                                ) : !ambassadorOverview || !ambassadorOverview.enabled ? (
                                    <p className="text-muted small fst-italic">The Ambassador Program is not enabled for this Workspace.</p>
                                ) : (
                                    <>
                                        <p className="text-muted small mb-3">
                                            Each member has their own referral link. Signups and subscriptions through any member's link count toward the Workspace's commission pool below.
                                        </p>
                                        <div className="d-flex gap-4 mb-4">
                                            <div>
                                                <div className="fw-bold fs-4">{ambassadorOverview.totals.referral_count}</div>
                                                <div className="text-muted small">Total Signups</div>
                                                {ambassadorOverview.totals.referral_count > 0 && (
                                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {ambassadorOverview.totals.individual_count} Individual · {ambassadorOverview.totals.workplace_count} Workspace
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <div className="fw-bold fs-4">{ambassadorOverview.totals.active_referral_count}</div>
                                                <div className="text-muted small">Active Subscriptions</div>
                                                {ambassadorOverview.totals.referral_count > 0 && (
                                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {ambassadorOverview.totals.monthly_count} Monthly · {ambassadorOverview.totals.annual_count} Annual
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <div className="fw-bold fs-4">${Number(ambassadorOverview.totals.total_commission).toFixed(2)}</div>
                                                <div className="text-muted small">Total Commission</div>
                                            </div>
                                        </div>
                                        {ambassadorOverview.members.length === 0 ? (
                                            <p className="text-muted small fst-italic">No members have a referral code yet.</p>
                                        ) : (
                                            <table className="table table-sm align-middle">
                                                <thead>
                                                    <tr>
                                                        <th>Member</th>
                                                        <th>Referral Link</th>
                                                        <th>Signups</th>
                                                        <th>Active</th>
                                                        <th>Plan / Billing</th>
                                                        <th>Share</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {ambassadorOverview.members.map(m => {
                                                        const joinUrl = `${window.location.origin}/join/${m.referral_token}`;
                                                        return (
                                                            <tr key={m.ambassador_id}>
                                                                <td>{m.UserAccount?.first_name} {m.UserAccount?.last_name}</td>
                                                                <td>
                                                                    <button
                                                                        className="btn btn-link btn-sm p-0 text-muted copy-referral-btn"
                                                                        onClick={() => navigator.clipboard.writeText(joinUrl)}
                                                                        title="Copy referral link"
                                                                    >
                                                                        <i className="bi bi-copy me-1"></i>Copy Link
                                                                    </button>
                                                                </td>
                                                                <td>{m.referral_count}</td>
                                                                <td>{m.active_referral_count}</td>
                                                                <td>
                                                                    {m.referral_count === 0 ? (
                                                                        <span className="text-muted">—</span>
                                                                    ) : (
                                                                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                                            {m.individual_count} Ind · {m.workplace_count} Wksp<br />
                                                                            {m.monthly_count} Mo · {m.annual_count} Yr
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td>${Number(m.total_commission).toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                        </div>
                    ) : myTeams.length === 0 ? (
                        <div className="text-center mt-5 py-5">
                            <i className="bi bi-people display-1 teams-empty-icon"></i>
                            <h2 className="teams-main-title mt-3">Ready to collaborate?</h2>
                            <p className="text-muted mb-4">You haven't joined or created any workspaces yet. Workspaces allow you to track bills and share insights with your colleagues.</p>
                            {canCreateWorkspace && !isCreating && (
                                <button className="btn btn-primary btn-lg px-4 shadow-sm" onClick={() => setIsCreating(true)}>Create a Workspace</button>
                            )}
                            {!workplaceSubscriber && (
                                <p className="text-muted small mb-0">
                                    <a href="/subscribe?plan=workplace">Subscribe to a Workspace plan</a> to create your own Workspace.
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="text-center mt-5 text-muted fst-italic">
                            Select a team from the left to manage members and settings.
                        </div>
                    )}

                </div>
            </div>

            {showFeedbackModal && (
                <CreateFeedbackRequestModal
                    teamId={selectedTeam.team_id}
                    clients={clients.filter(c => !c.archived)}
                    isStakeholderGroup={!isClientLabelType}
                    onClose={() => setShowFeedbackModal(false)}
                    onCreated={(clientId) => loadFeedbackRequests(clientId)}
                />
            )}

            {editOrgData && (
                <EditLinkedOrgModal
                    org={editOrgData}
                    onClose={() => setEditOrgData(null)}
                    onSaved={() => { setEditOrgData(null); viewTeamDetails(selectedTeam.team_id); }}
                />
            )}

            {showBulkReassign && selectedTeam && (
                <div className="tw-modal-backdrop" onClick={() => { setShowBulkReassign(false); setBulkFrom(''); setBulkTo(''); setBulkResult(null); }}>
                    <div className="tw-modal-box" onClick={e => e.stopPropagation()}>
                        <h6 className="fw-bold mb-1" style={{ color: '#1a2744' }}>Bulk Reassign</h6>
                        <p className="small text-muted mb-3">Transfer all bill and task assignments from one member to another.</p>
                        <div className="d-flex flex-column gap-2">
                            <select className="form-select form-select-sm" value={bulkFrom} onChange={e => setBulkFrom(e.target.value)}>
                                <option value="">From member…</option>
                                {selectedTeam.TeamMembers.map(m => (
                                    <option key={m.user_id} value={m.user_id}>{m.ProfileInfo.first_name} {m.ProfileInfo.last_name}</option>
                                ))}
                            </select>
                            <div className="text-center text-muted small"><i className="bi bi-arrow-down" /></div>
                            <select className="form-select form-select-sm" value={bulkTo} onChange={e => setBulkTo(e.target.value)}>
                                <option value="">To member…</option>
                                {selectedTeam.TeamMembers.filter(m => m.user_id !== bulkFrom).map(m => (
                                    <option key={m.user_id} value={m.user_id}>{m.ProfileInfo.first_name} {m.ProfileInfo.last_name}</option>
                                ))}
                            </select>
                        </div>
                        {bulkResult && (
                            <p className="small text-success mt-3 mb-0">
                                Done — {bulkResult.moved} assignment{bulkResult.moved !== 1 ? 's' : ''} moved{bulkResult.skipped > 0 ? `, ${bulkResult.skipped} skipped (already assigned)` : ''}.
                            </p>
                        )}
                        <div className="d-flex gap-2 mt-3 justify-content-end">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => { setShowBulkReassign(false); setBulkFrom(''); setBulkTo(''); setBulkResult(null); }}>Cancel</button>
                            <button className="btn btn-sm btn-primary" disabled={!bulkFrom || !bulkTo} onClick={handleBulkReassign}>Reassign</button>
                        </div>
                    </div>
                </div>
            )}

            {detailTaskId !== null && selectedTeam && (() => {
                const task = teamTasks.find(t => t.id === detailTaskId);
                if (!task) return null;
                return (
                    <TaskDetailModal
                        task={{ ...task, TeamAccount: { team_name: selectedTeam.team_name } }}
                        userId={auth.user.user_id}
                        canManage={canManage}
                        onClose={() => setDetailTaskId(null)}
                        onUpdated={fields => applyTaskFields(task, fields)}
                        onStepsChanged={counts => setTeamTasks(prev => prev.map(t => t.id === task.id ? { ...t, ...counts } : t))}
                    />
                );
            })()}

            {showAddTask && selectedTeam && (
                <AddTaskModal
                    teams={[{
                        team_id: selectedTeam.team_id,
                        team_name: selectedTeam.team_name,
                        can_assign: canManage,
                        members: selectedTeam.TeamMembers.map(m => ({
                            user_id: m.user_id,
                            name: `${(m.ProfileInfo || m.UserAccount)?.first_name || ''} ${(m.ProfileInfo || m.UserAccount)?.last_name || ''}`.trim(),
                        })),
                    }]}
                    userId={auth.user.user_id}
                    onCreated={task => {
                        if (task.rulemaking_ref) setTeamRulemakingTasks(prev => [...prev, task]);
                        if (task.bill_id) {
                            setBillTasks(prev => prev[task.bill_id] ? { ...prev, [task.bill_id]: [...prev[task.bill_id], task] } : prev);
                            setTrackedBillsOverview(prev => prev.map(b =>
                                b.bill_id === task.bill_id ? { ...b, open_task_count: b.open_task_count + 1 } : b
                            ));
                        }
                        if (teamTab === 'tasks') fetchTeamTasks(taskDashFilter);
                    }}
                    onClose={() => setShowAddTask(false)}
                />
            )}

            {selectedReportId && selectedTeam && (
                <ReportDetailModal
                    reportId={selectedReportId}
                    teamId={selectedTeam.team_id}
                    canManage={canManage}
                    onClose={() => setSelectedReportId(null)}
                    onDeleted={() => { setSelectedReportId(null); fetchReports(selectedTeam.team_id); }}
                />
            )}
        </div>
    );
}